// /src/renderer/analysis/rendering/Panels/renderers/DICOMPanel.tsx
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  useSyncExternalStore,
  ReactElement,
} from 'react';
import '../PanelContainer.scss';
import PanelOverlay from './PanelOverlay';
import {
  PanelCommonProps,
  PanelComponentKeyType,
} from 'config/panelConfig';
import SimpleContextMenu, {
  SimpleContextMenuItem,
} from '@components/ContextMenu/SimpleContextMenu';
import { store } from '@root/store';

import ThicknessContextMenu from '@features/analysis/analysisModal/ThicknessContextMenu/ThicknessContextMenu';
import {
  ChamberViewProps,
  defaultPreset,
  EditNames,
  PanelState,
  Segmentation,
  SegmentationControllerType,
  singleCrosshairsAnalysisType,
  ThicknessMethodType,
} from 'config/analysisConfig';

import {
  CT_PRESET_EDITOR_NAME,
  PANEL_ADJUSTMENT_NAMES,
} from 'config/labels';

import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { usePanelContainer } from '../usePanelContainer';
import { TOOL_ITEM_CONFIG, ToolItemConfig } from 'config/toolsConfig';

import {
  onImageRendered,
  // applyPanelState,
  unregisterSynchronizeViewport,
  registerSynchronizeViewport,
  addRequestPool,
  getVolumeMinMax,
  setDICOMViewportVolume,
  registerEvents,
  unregisterEvents,
  updateViewportToWorld,
  getCanvasCoordinatesFromWorld,
  getCanvasToWorld,
  getRequestSegmentation,
  updateLabelmapViewport,
  updateRegulatorSegmentationViewport,
  // updateViewportWindowLevelWidth,
  updateViewportsWindowLevelWidth,
  updateViewportCamera,
  registerToolGroupForViewport,
  registerToolGroupForChamber,
  setChamberViewEvents,
  getGuideLines,
  rotateAroundAxis,
  getOrthoNormal,
  getCenters,
  setupGuideViewport,
  getVolumeIndex,
  captureImage,
  normalize,
  getInvisibleSection,
  onCameraModified,
  removeLabelmapViewport,
  detachVolume,
  unregisterToolGroupForViewport,
  unsetInvisibleSection,
  requestPixelsPerMM,
  updateViewportOrientation,
  toggleCrosshairsTool,
  updateLabelmapSegment,
  getWorldCoordinate,
  updateModifierOverlay,
  updateViewportCameraDeltaMove,
  lastChamberTransform,
  updateViewportThickness,
  setVOIAll,
  getViewport,
  fitDicomViewportsToScreen,
  fitChamberViewportToImageSize,
  getChamberFitParallelScale,
  deepClone,
  updateViewportThicknessMethod,
  scalePoint3,
  length,
  handleVoiClampEvent,
  deactivateExclusiveTools,
  isToolInteracting,
  getViewportIdsEnabled,
  toggleManualSegVisible,
  ensureManualSegmentationViewport,
} from './CornerstoneManager';

import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useCTPresetModal } from '@features/analysis/analysisModal/CTPresetModal/CTPresetModal';
import DistanceScaleBarLayout from '@components/DistanceScaleBar/DistanceScaleBar';
import useAuth from '@hooks/Auth/useAuth';
import {
  renderers,
  scheduleRender,
} from '@features/analysis/rendering/Panels/renderState';
import {
  getChamberGuidePosition,
  getChamberViewDirectionBackgroundFocalPoint,
  getChamberViewDirectionBackgroundSeriesId,
  resolveChamberGuidePanFromSeries,
  resolveNextChamberGuidePanState,
  resolveChamberReentryState,
  withChamberViewDirectionBackgroundFocalPoint,
} from '@features/analysis/rendering/Panels/chamberGuideState';
import {
  CHAMBER_AXIS_APPLY_VIEWPORTS,
  CHAMBER_AXIS_PICK_VIEWPORTS,
  type ChamberAxisPickMode,
  calculateChamberAxisBasisRotation,
  copyChamberAxisSavedInfoValue,
  getChamberAxisControllerModel,
  getChamberAxisMidpoint,
  getChamberAxisSnapshot,
  isChamberAxisPickViewport,
  markChamberAxisApplied,
  projectChamberAxisMarkers,
  readChamberAxisBasis,
  resetChamberAxisState,
  resolveChamberAxisActiveViewport,
  setChamberAxisError,
  setChamberAxisPickMode,
  setChamberAxisWorldPoint,
  subscribeChamberAxisStore,
  writeChamberAxisBasis,
  worldToChamberAxisCenterCoordinate,
} from '@features/analysis/rendering/Panels/chamberAxisVisualController';
import { ChamberAxisController } from '@features/analysis/rendering/Panels/ChamberAxisController';
import { runWorkerTask } from '@features/analysis/rendering/Panels/workerPool';
import { postSeriesParameterChamber4 } from '@api/series';
import Crosshair from '@components/Crosshair/Crosshair';
import { useConfig } from 'config/useConfig';
import SpacingContextMenu from '@features/analysis/analysisModal/SpacingContextMenu/SpacingContextMenu';
import ImageSizeContextMenu from '@features/analysis/analysisModal/ImageSizeModal/ImageSizeContextMenu';
import { useBufferPool } from './vtkManagers';
import { CalciumAnalysisResult, SavedInfo } from '@models/Parameter';
import AdditionalSliceContextMenu from '@features/analysis/analysisModal/AdditionalSliceContextMenu/AdditionalSliceContextMenu';

import { configStore } from 'config/configStore';
import { alignFunction } from 'utils/captureUtils';
import { useTool } from '@hooks/Tool/useTool';
import { ToolId } from 'config/toolDefinitionsConfig';
import { useEFAnalysis } from '@hooks/useAnalysisForPanel/useEFAnalysis';
import { clamp, throttle } from 'lodash';
import { usePanelOverlayText } from '@hooks/useAnalysisForPanel/usePanelOverlayText';
import { updateOtherViews } from '@hooks/useAnalysisForPanel/useDicomUtils';
import { useSegmentManager } from '@features/analysis/segmentManager';
import {
  CHAMBER_OVERLAY_WHEEL_RANGES,
  getNextChamberOverlayWheelValue,
} from './chamberOverlayWheelControls';
import {
  CHAMBER_ROTATION_HINT_TIMEOUT_MS,
  getChamberRotationHintRows,
  type ChamberRotationHintMode,
} from './chamberRotationHint';
import {
  resetChamberScoutDisplayRollConfig,
  resetChamberScoutDisplayRollProps,
} from './chamberResetState';
import { convertFFRToWorld } from '@features/view/Surface3DUtilities';

// import useViewResizeObserver from './useViewResizeObserver';
// Series Title
// (Patient Name) - (Patient Age) - (Acquisition Date) - (Acquisition Time) - (Series Description)
// text 표기, 정보 없으면 delimiter 제외, 공백

// Series Description
// -(Acquisition Date) (Acquisition Time)
// -(Accession Number)
// -(Study Description)
// -(Series Description)
// -(Image Comments)
// -Series: (Series Number)
// text 표기, 정보 없으면 공백

// Patient Description
// -(Institution Name)
// -(Patient Name)
// -(Patient ID)
// -(Patient Birth Date) (Patient Sex) (Patient Age)

//Image Controller
//-Images: (현재 인덱스) / (시리즈 개수)
//-(Axial) (LB클릭 시 OS6 컨텍스트 메뉴)
//-Zoom: (배율)% (LB클릭 시 OS7 컨텍스트 메뉴)
//-Thickness: (현재 방법)(LB클릭 시 OS8 모달)

// Acquisition Device Description
// -X: (mouseX) Y: (mouseY) Value: (mouse Pixel Value) (Image hover 시)
// -(X-Ray Tube Current) (KVP)
// -W/L (Window Width)/(Window Center)(LB클릭 시 OS9 컨텍스트 메뉴)

// Single대비 MPR에서 뺄거
// Images 표시안함
// C-Arm Angulation 표시
// Activation패널 하이라이트
type DICOMPanelPopup = {
  [Key in keyof typeof PANEL_ADJUSTMENT_NAMES]?: boolean;
};
const initialPopup: DICOMPanelPopup = {
  projection: false,
  zoom: false,
  thickness: false,
  preset: false,
  spacing: false,
  imageSize: false,
  additionalSlice: false,
};
const CHAMBER_ADDITIONAL_SLICE_MIN =
  CHAMBER_OVERLAY_WHEEL_RANGES.additionalSlice.min;
const CHAMBER_ADDITIONAL_SLICE_MAX =
  CHAMBER_OVERLAY_WHEEL_RANGES.additionalSlice.max;
const clampAdditionalSlice = (value: number) =>
  Math.min(
    CHAMBER_ADDITIONAL_SLICE_MAX,
    Math.max(CHAMBER_ADDITIONAL_SLICE_MIN, value),
  );
const toEffectiveAdditionalSlice = (value: number) =>
  value * 0.5;

const getSafeChamberIndex = (index: number | undefined, length: number) => {
  if (length <= 0) return -1;
  if (typeof index !== 'number' || !Number.isFinite(index)) return 0;
  return Math.min(Math.max(Math.round(index), 0), length - 1);
};

interface DistanceInfo {
  dimensions: [number, number, number];
  spacing: [number, number, number];
}


function formatWindowLevelOverlayValue(value: number | string | null | undefined) {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value ?? '');
  return String(Math.round(numeric));
}

const DICOMPanelZoomItems = [
  TOOL_ITEM_CONFIG.FitToScreen,
  TOOL_ITEM_CONFIG.OneOnOneResolution,
] as ToolItemConfig[];
const DICOM_ZOOM_VIEWPORT_KEYS: PanelComponentKeyType[] = [
  'Axial',
  'Coronal',
  'Sagittal',
];
type ChamberDicomViewportKey =
  | 'Axial'
  | 'FourChamber'
  | 'TwoChamber'
  | 'ShortAxis';

const CHAMBER_DICOM_VIEWPORT_KEYS: ChamberDicomViewportKey[] = [
  'Axial',
  'FourChamber',
  'TwoChamber',
  'ShortAxis',
];

function isChamberDicomViewport(
  componentKey: PanelComponentKeyType | undefined,
): componentKey is ChamberDicomViewportKey {
  return (
    !!componentKey &&
    CHAMBER_DICOM_VIEWPORT_KEYS.includes(
      componentKey as ChamberDicomViewportKey,
    )
  );
}

function toSVGPoint({
  svg,
  clientX,
  clientY,
}: {
  svg: SVGSVGElement;
  clientX: number;
  clientY: number;
}) {
  const pt = svg.createSVGPoint()!;
  pt.x = clientX;
  pt.y = clientY;
  return pt.matrixTransform(svg.getScreenCTM()!.inverse());
}

function parseChamberAxis({
  componentKey,
  savedInfo,
}: {
  componentKey: PanelComponentKeyType;
  savedInfo: SavedInfo;
}) {
  const { Axial, Chamber4 } = savedInfo.Chamber;
  const chamVar = componentKey === 'Axial' ? Axial : Chamber4;
  const center: [number, number, number] = [
    -chamVar[3],
    -chamVar[4],
    chamVar[5],
  ];
  const normal: [number, number, number] = [
    chamVar[0],
    chamVar[1],
    -chamVar[2],
  ];
  const right: [number, number, number] = [chamVar[6], chamVar[7], -chamVar[8]];
  const up: [number, number, number] = [chamVar[9], chamVar[10], -chamVar[11]];
  let viewUp: [number, number, number] = [1e-6, 1 + 1e-6, 1e-6];
  let viewPlaneNormal: [number, number, number] = [1e-6, 1e-6, -1 + 1e-6];
  switch (componentKey) {
    case 'FourChamber':
      viewUp = up.map((v) => -v) as [number, number, number];
      viewPlaneNormal = normal.map((v) => -v) as [number, number, number];
      break;
    case 'TwoChamber':
      viewUp = normal;
      viewPlaneNormal = up.map((v) => -v) as [number, number, number];
      break;
    case 'ShortAxis':
      viewUp = normal;
      viewPlaneNormal = right.map((v) => -v) as [number, number, number];
      break;
  }
  viewUp = normalize(viewUp);
  viewPlaneNormal = normalize(viewPlaneNormal);
  return {
    center,
    viewUp,
    viewPlaneNormal,
  };
}

const DICOMPanel: React.FC<PanelCommonProps> = ({
  className,
  panelConfig,
  possibleProjection,
  topLeftContent,
  topRightContent,
}: PanelCommonProps) => {
  const ref = useRef<HTMLDivElement>(null);
  // const wasCtrlPressedRef = useRef(false);
  const refSVG = useRef<SVGSVGElement>(null);
  const { componentKey } = panelConfig;
  const chamberAxisState = useSyncExternalStore(
    subscribeChamberAxisStore,
    getChamberAxisSnapshot,
    getChamberAxisSnapshot,
  );
  const [prevComponentKey, setPrevComponentKey] =
    useState<PanelComponentKeyType>('Empty');
  const [open, setOpen] = useState<DICOMPanelPopup>(initialPopup);
  const [chamberRotationHint, setChamberRotationHint] = useState<
    ChamberRotationHintMode | undefined
  >();
  const chamberRotationHintTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [pointInfo, setPointInfo] =
    useState<[number, number, number, number]>();
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });
  const [worldInfo, setWorldInfo] = useState<{
    x: number;
    y: number;
    z: number;
  }>();
  const [canvasInfo, setCanvasInfo] = useState<{ x: number; y: number }>({
    x: dimensions.width / 2,
    y: dimensions.height / 2,
  });
  const [pixelsPerMM, setPixelsPerMM] = useState<number>(1);
  // const [isEnabled,setIsEnabled] = useState(true);
  // const [radiusMM, setRadiusMM] = useState<number>(5)
  // const [distanceBarProps, setDistanceBarProps] =useState<{
  //   dimensions :[number,number,number],
  //   spacing : [number,number,number],
  //   origin:[number,number,number],
  // }|undefined>()
  const selectedSegKey = useRef<SegmentationControllerType>();
  const { config, setData } = useConfig();

  const { accessToken } = useAuth();
  const {
    selectedSeries,
    selectedStudySeries,
    selectedSeriesImageTag,
    defaultPanelStates,
    CTBottomRightSubInfo,
    seriesSelections,
  } = useBrowserPage();
  const selectedSeriesId = selectedSeries?.id;
  const {
    selectedTool,
    currentVOI,
    currentPreset,
    // selectedSeriesUid,
    switchConfigs,
    globalToolState,
    // syncPanelComponentKey,
    isPreview,
    updateSwitchConfigs,
    handleLayoutStateLoading,
    panelToolState,
    selectedLayoutConfig,
    updateWindowLevelWidth,
    updateVOIInvert,
    selectedAnalysisDefault,
  } = useAnalysisPage();
  const { isEF, updateEFViewport } = useEFAnalysis(componentKey);
  const currentEFSeriesIndex = useRef<number>(-1);
  const {
    analysisType,
    panelStates,
    fullscreenPanelComponentKey,
    swapLayoutPanelConfig,
    updatePanelState,
    updateSelectedPanelConfig,
    updateFullscreenPanelComponentKey,
    selectedPanelComponentKey,
  } = usePanelContainer();

  const {
    midLeftContentRRPercent,
  } = usePanelOverlayText({componentKey});

  const { handleToolActivate } = useTool();
  const { bufferPool, setBufferPool } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  const openCTPreset = useCTPresetModal();
  const {mode:manualMode} = useSegmentManager();
  const isAxisVisible = globalToolState?.toggles.ToggleAxes;
  const isDicomZoomSyncEnabled =
    globalToolState?.toggles.ToggleDicomZoomSync ?? true;
  const isDicomPanSyncEnabled =
    globalToolState?.toggles.ToggleDicomPanSync ?? true;
  const isDicomPanInteractionRef = useRef(false);
  const { renderingQuality } = config.generalSetting;
  const lowQuality = renderingQuality !== 'high';
  const { windowLevel, windowWidth, invert } = currentVOI;
  const lastValidVoiRangeMap = new Map<
    string,
    { lower: number; upper: number }
  >();

  const throttledUpdateWindowLevelWidth = throttle(
    (windowLevel: number, windowWidth: number) => {
      updateWindowLevelWidth([windowLevel, windowWidth]);
    },
    16,
  );

  const zoomScale = analysisType === 'chamber' ? 0.8 : 1;
  const fitDicomViewportsToScreenOnNextFrame = useCallback(
    (repeat = false) => {
      if (analysisType === 'chamber') return;
      if (!DICOM_ZOOM_VIEWPORT_KEYS.includes(componentKey)) return;
      if (
        fullscreenPanelComponentKey &&
        fullscreenPanelComponentKey !== 'Empty' &&
        !DICOM_ZOOM_VIEWPORT_KEYS.includes(fullscreenPanelComponentKey)
      )
        return;
      fitDicomViewportsToScreen({
        syncToAxial: isDicomZoomSyncEnabled,
        zoomScale,
        repeat,
      });
    },
    [
      analysisType,
      componentKey,
      fullscreenPanelComponentKey,
      isDicomZoomSyncEnabled,
      zoomScale,
    ],
  );
  const prevDicomZoomSyncEnabledRef = useRef(isDicomZoomSyncEnabled);

  useEffect(() => {
    if (!ref.current) return;

    const currentDiv = ref.current;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect) {
          const width = Math.round(entry.contentRect.width);
          const height = Math.round(entry.contentRect.height);
          setDimensions((prev) =>
            prev.width === width && prev.height === height
              ? prev
              : { width, height },
          );
        }
      });
    });

    if (currentDiv) {
      observer.observe(currentDiv);
    }

    return () => {
      if (currentDiv) {
        observer.unobserve(currentDiv);
      }
    };
  }, []);

  // const forDistanceBar = getPixelSpacing(componentKey);
  // 패널 스위칭이 가능한 상태인지
  // const isLock =!isPreview && globalToolState?.toggles.ToggleSync;
  // Sync라면 refComponentKey이 있는상태.
  // 내 값은 안바꾼다는거(대표 패널 제외)
  const refComponentKey =
    componentKey === 'Direction' ? selectedPanelComponentKey! : componentKey;
  // const refComponentKey =
  //   analysisType === 'chamber'
  //     ? selectedPanelComponentKey === 'VR'
  //       ? isLock
  //         ? (syncPanelComponentKey ?? componentKey)
  //         : componentKey!
  //       : componentKey === 'Direction'
  //         ? (selectedPanelComponentKey ?? componentKey)
  //         : componentKey
  //     : isLock
  //       ? (syncPanelComponentKey ?? componentKey)
  //       : componentKey;

  // RefPanel의 상태
  const panelState = panelStates[refComponentKey];
  const refPanelState = useRef(panelState);
  const panelStatesRef = useRef(panelStates);
  // const prevRefComponentKey = useRef<PanelComponentKeyType>('Axial');

  //패널 상태 업뎃
  const updatePanelStateCB = useCallback(
    (key: keyof PanelState | string, value: PanelState[keyof PanelState]) => {
      if (key === 'pixelsPerMM') setPixelsPerMM(value as number);
      else updatePanelState(refComponentKey, key, value);
    },
    [refComponentKey, updatePanelState],
  );
  const handleUpdatePanelState = throttle(
    (key: keyof PanelState | string, value: PanelState[keyof PanelState]) => {
      updatePanelStateCB(key, value);
    },
    16,
  );
  const updateViewportPanelState = useCallback(
    (
      viewportId: PanelComponentKeyType,
      key: keyof PanelState | string,
      value: PanelState[keyof PanelState],
    ) => {
      if (viewportId === refComponentKey && key === 'pixelsPerMM') {
        setPixelsPerMM(value as number);
        return;
      }
      updatePanelState(viewportId, key, value);
    },
    [refComponentKey, updatePanelState],
  );
  // 내 상태들 좋댓구알
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      refPanelState.current =
        store.getState().analysis.panelStates[refComponentKey];
    });
    return unsubscribe;
  }, [store, refComponentKey]);

  useEffect(() => {
    panelStatesRef.current = panelStates;
  }, [panelStates]);

  const guideUpdateCallback = useCallback(
    (
      state: Partial<ChamberViewProps>,
      targetViewportId = selectedPanelComponentKey,
    ) => {
      console.log('왔는데?', targetViewportId, componentKey);
      scheduleRender('Direction', {
        type: 'guideUpdate',
        target: targetViewportId,
        state,
      });
    },
    [selectedPanelComponentKey],
  );

  const showChamberRotationHint = useCallback(
    (hint: ChamberRotationHintMode) => {
      if (componentKey !== 'Direction') return;
      setChamberRotationHint(hint);
      if (chamberRotationHintTimerRef.current) {
        clearTimeout(chamberRotationHintTimerRef.current);
      }
      chamberRotationHintTimerRef.current = setTimeout(() => {
        // setChamberRotationHint(undefined);
        chamberRotationHintTimerRef.current = null;
      }, CHAMBER_ROTATION_HINT_TIMEOUT_MS);
    },
    [componentKey],
  );

  useEffect(() => {
    return () => {
      if (chamberRotationHintTimerRef.current) {
        clearTimeout(chamberRotationHintTimerRef.current);
      }
    };
  }, []);

  const refreshDirectionGuideViewport = useCallback(
    (
      targetViewportId: PanelComponentKeyType,
      chamberViewProps: Partial<ChamberViewProps>,
    ) => {
      const { position, guideDelta, guidePlaneNormal, guideUp } =
        chamberViewProps;
      if (!position || !guidePlaneNormal || !guideUp) return false;
      const directionBackgroundSeriesId =
        getChamberViewDirectionBackgroundSeriesId(chamberViewProps);
      const storedDirectionBackgroundFocalPoint =
        getChamberViewDirectionBackgroundFocalPoint(chamberViewProps);
      const canUseStoredDirectionBackground =
        !directionBackgroundSeriesId ||
        !selectedSeriesId ||
        directionBackgroundSeriesId === selectedSeriesId;
      const fallbackDirectionBackgroundFocalPoint = guideDelta
        ? (position.map((v, i) => v - guideDelta[i]) as [
            number,
            number,
            number,
          ])
        : position;
      const directionBackgroundFocalPoint =
        canUseStoredDirectionBackground && storedDirectionBackgroundFocalPoint
          ? storedDirectionBackgroundFocalPoint
          : fallbackDirectionBackgroundFocalPoint;

      setupGuideViewport({
        viewportId: targetViewportId,
        planeNormal: guidePlaneNormal,
        up: guideUp,
        focalPoint: directionBackgroundFocalPoint,
      });
      const scoutDisplayRollRad = chamberViewProps.scoutDisplayRollRad ?? 0;
      if (scoutDisplayRollRad !== 0) {
        lastChamberTransform(
          'Direction',
          scoutDisplayRollRad,
          false,
          false,
        );
      }
      guideUpdateCallback(chamberViewProps, targetViewportId);
      return true;
    },
    [guideUpdateCallback, selectedSeriesId],
  );

  const getChamberOverlayWheelTarget = useCallback(() => {
    const targetViewportId =
      componentKey === 'Direction' ? selectedPanelComponentKey : componentKey;
    return isChamberDicomViewport(targetViewportId)
      ? targetViewportId
      : undefined;
  }, [componentKey, selectedPanelComponentKey]);

  const getChamberOverlayWheelProps = useCallback(
    (targetViewportId: ChamberDicomViewportKey | undefined) => {
      if (!targetViewportId) return undefined;
      return panelStates[targetViewportId]?.chamberViewProps;
    },
    [panelStates],
  );

  const updateThicknessValue = useCallback(
    (value: number) => {
      const value2 = value === 0.1 ? value : Math.min(Math.round(value), 10);
      if (analysisType === 'chamber') {
        const targetViewportId = getChamberOverlayWheelTarget();
        if (!targetViewportId) return;
        setData({
          chamberCaptureThickness: {
            ...config.chamberCaptureThickness,
            [targetViewportId]: value2,
          },
        });
      }
      handleUpdatePanelState('thickness', value2);
    },
    [
      analysisType,
      config.chamberCaptureThickness,
      getChamberOverlayWheelTarget,
      handleUpdatePanelState,
      setData,
    ],
  );

  const updateChamberSpacingValue = useCallback(
    (value: number) => {
      const targetViewportId = getChamberOverlayWheelTarget();
      const chamberViewProps = getChamberOverlayWheelProps(targetViewportId);
      if (!targetViewportId || !chamberViewProps) return;
      const spacing = Math.min(
        CHAMBER_OVERLAY_WHEEL_RANGES.spacing.max,
        Math.max(CHAMBER_OVERLAY_WHEEL_RANGES.spacing.min, value),
      );
      setData({
        chamberCaptureSpacing: {
          ...config.chamberCaptureSpacing,
          [targetViewportId]: spacing,
        },
      });
      guideUpdateCallback(
        {
          ...chamberViewProps,
          spacing,
        },
        targetViewportId,
      );
    },
    [
      config.chamberCaptureSpacing,
      getChamberOverlayWheelProps,
      getChamberOverlayWheelTarget,
      guideUpdateCallback,
      setData,
    ],
  );

  const updateChamberAdditionalSliceValue = useCallback(
    (value: number) => {
      const targetViewportId = getChamberOverlayWheelTarget();
      const chamberViewProps = getChamberOverlayWheelProps(targetViewportId);
      if (!targetViewportId || !chamberViewProps) return;
      const additionalSlice = clampAdditionalSlice(value);
      setData({
        chamberCaptureAdditionalSlice: {
          ...config.chamberCaptureAdditionalSlice,
          [targetViewportId]: additionalSlice,
        },
      });
      guideUpdateCallback(
        {
          ...chamberViewProps,
          additionalSlice,
        },
        targetViewportId,
      );
    },
    [
      config.chamberCaptureAdditionalSlice,
      getChamberOverlayWheelProps,
      getChamberOverlayWheelTarget,
      guideUpdateCallback,
      setData,
    ],
  );

  const updateChamberImageSizeValue = useCallback(
    (value: number) => {
      const targetViewportId = getChamberOverlayWheelTarget();
      const chamberViewProps = getChamberOverlayWheelProps(targetViewportId);
      if (!targetViewportId || !chamberViewProps) return;
      const imageSize = value;
      setData({
        chamberCaptureImageSize: {
          ...config.chamberCaptureImageSize,
          [targetViewportId]: imageSize,
        },
      });
      guideUpdateCallback(
        {
          ...chamberViewProps,
          imageSize,
        },
        targetViewportId,
      );
    },
    [
      config.chamberCaptureImageSize,
      getChamberOverlayWheelProps,
      getChamberOverlayWheelTarget,
      guideUpdateCallback,
      setData,
    ],
  );

  const handleChamberOverlayWheel = useCallback(
    (
      event: React.WheelEvent,
      currentValue: number | undefined,
      range: Parameters<typeof getNextChamberOverlayWheelValue>[2],
      onChangeValue: (value: number) => void,
    ) => {
      if (event.deltaY === 0) return;
      event.preventDefault();
      event.stopPropagation();
      const nextValue = getNextChamberOverlayWheelValue(
        currentValue,
        event.deltaY,
        range,
      );
      if (nextValue === undefined || nextValue === currentValue) return;
      onChangeValue(nextValue);
    },
    [],
  );

  const pixelSpacing = useMemo(() => {
    const scale = lowQuality ? 2 : 1;
    return [
      selectedSeriesImageTag?.pixelSpacing ?? 1,
      selectedSeriesImageTag?.pixelSpacing ?? 1,
      selectedSeriesImageTag?.planarSpacingInterpolated ?? 1,
    ].map((v) => v * scale) as [number, number, number];
  }, [selectedSeriesImageTag, config.generalSetting.renderingQuality]);

  useEffect(() => {
    if (analysisType === 'chamber' && componentKey === 'Direction') {
      if (
        config.chamberCaptureWindowLevel !== windowLevel ||
        config.chamberCaptureWindowWidth !== windowWidth
      ) {
        setData({
          chamberCaptureWindowLevel: windowLevel,
          chamberCaptureWindowWidth: windowWidth,
        });
      }
    }
  }, [analysisType, windowLevel, windowWidth, invert]);
  renderers[componentKey] = {
    render: (args?: any) => {
      const { type } = args;
      if (type === 'focus') {
        let world = args.world;
        if (!world) return;
        const shouldCenterView =
          args.centerView === true ||
          args.mode === 'mouse' ||
          args.mode === 'cpr' ||
          (args.request !== true && args.centerView !== false);
        if (
          analysisType === 'FFR' &&
          selectedSeriesId &&
          args.mode === 'mouse' &&
          !config.EditMode
        ) {
          const convertedWorld = convertFFRToWorld(selectedSeriesId, world);
          if (!convertedWorld) return;
          world = convertedWorld;
        }
        if (args.request) {
          requestAnimationFrame(() => {
            updateViewportToWorld(componentKey, world, {
              centerView: shouldCenterView,
            });
          });
        } else {
          updateViewportToWorld(componentKey, world, {
            centerView: shouldCenterView,
          });
        }

        setWorldInfo({ x: world[0], y: world[1], z: world[2] });
        const canvasCoords = getCanvasCoordinatesFromWorld(componentKey, world);
        if (canvasCoords) {
          setCanvasInfo(canvasCoords);
        }
      }
      if (type === 'pixelChange') {
        console.log('띠용');
        const value = requestPixelsPerMM({ viewportId: componentKey });
        setPixelsPerMM(value);
      }
      if (type === 'updateLabelmap') {
        // const {requestSegmentation}  = args;
        const { update2DLabel } = args;
        if (!ref.current) return;
        if (prevComponentKey === 'Empty') return;
        if (
          analysisType === 'chamber' ||
          analysisType === 'preview' ||
          analysisType === 'MPR'
        )
          return;
        const { segmentation } = panelStates['Surface3D'];
        if (!selectedSeries || !segmentation) return;

        if (update2DLabel) {
          const seg = getRequestSegmentation(analysisType!, segmentation);
          const keys = Object.keys(seg) as (keyof typeof seg)[];
          const isFFREditMode = analysisType === 'FFR' && config.EditMode;
          const visibility = isFFREditMode
            ? toggleManualSegVisible(selectedSeries.id, args.visibility)
            : undefined;

          let clonedSegmentation = deepClone(segmentation) as Segmentation;
          keys.forEach((key) => {
            clonedSegmentation = {
              ...clonedSegmentation,
              [key]: {
                ...clonedSegmentation[key],
                visibility: isFFREditMode
                  ? visibility
                  : clonedSegmentation[key].visibility && config.label.axial,
              },
            };
            updateLabelmapSegment({
              ...(isFFREditMode && {
                segmentationId: `segmentation_${selectedSeries.id}_labelmap`,
              }),
              segmentTarget: key,
              segmentations: clonedSegmentation,
            });
          });
        } else {
          console.log('뷰포트가 있어서 내가 먼저 받았어', componentKey);
           updateLabelmapViewport(
             componentKey,
             selectedSeries.id,
             getRequestSegmentation(analysisType, segmentation),
             isEF || lowQuality,
           );
        }
      }
      if (type === 'afterVOIModified') {
        const { windowLevel, windowWidth } = args;
        throttledUpdateWindowLevelWidth(windowLevel, windowWidth);
      }
      if (type === 'transformViewport') {
        if (componentKey === 'Direction') return;
        //이건 본체가 처리해버리기
        const { method } = args;
        const { chamberViewProps } =
          panelStates[selectedPanelComponentKey! as PanelComponentKeyType];
        if (!chamberViewProps) return;
        const { flipHorizontal, flipVertical, rollRad } = chamberViewProps;

        let newFlipVertical = flipVertical;
        let newFlipHorizontal = flipHorizontal;
        let newRollRad = rollRad;

        const rotAmount = 90.0 * (Math.PI / 180.0);

        // //돌린다.
        switch (method) {
          case 'flipVertical':
            newFlipVertical = !newFlipVertical;
            break;
          case 'flipHorizontal':
            newFlipHorizontal = !newFlipHorizontal;
            break;
          case 'rotateCW90':
            newRollRad += rotAmount;
            break;
          case 'rotateCCW90':
            newRollRad -= rotAmount;
            break;
          case 'resetRotation':
            break;
          default:
            return;
        }

        const { axis } = alignFunction(
          chamberViewProps.viewNormal!,
          chamberViewProps.normal!,
          chamberViewProps.alignAxis!,
        );
        updateViewportCamera({
          viewportId: selectedPanelComponentKey!,
          focalPoint: chamberViewProps.centers![chamberViewProps.index!],
          viewPlaneNormal: chamberViewProps.viewNormal!,
          viewUp: axis,
          handleUpdatePanelState: (key, value) =>
            updateViewportPanelState(selectedPanelComponentKey!, key, value),
        });

        const res = lastChamberTransform(
          selectedPanelComponentKey!,
          newRollRad,
          newFlipVertical,
          newFlipHorizontal,
          (key, value) =>
            updateViewportPanelState(selectedPanelComponentKey!, key, value),
        );

        if (newRollRad != rollRad) {
          setData({
            chamberCaptureRollAngle: {
              ...config.chamberCaptureRollAngle,
              [componentKey as
                | 'FourChamber'
                | 'TwoChamber'
                | 'ShortAxis'
                | 'Axial']: newRollRad,
            },
          });
        }
        if (newFlipVertical != flipVertical) {
          setData({
            chamberCaptureFlipVertical: {
              ...config.chamberCaptureFlipVertical,
              [componentKey as
                | 'FourChamber'
                | 'TwoChamber'
                | 'ShortAxis'
                | 'Axial']: newFlipVertical,
            },
          });
        }
        if (newFlipHorizontal != flipHorizontal) {
          setData({
            chamberCaptureFlipHorizontal: {
              ...config.chamberCaptureFlipHorizontal,
              [componentKey as
                | 'FourChamber'
                | 'TwoChamber'
                | 'ShortAxis'
                | 'Axial']: newFlipHorizontal,
            },
          });
        }

        updatePanelState(selectedPanelComponentKey!, 'chamberViewProps', {
          ...chamberViewProps,
          flipHorizontal: newFlipHorizontal,
          flipVertical: newFlipVertical,
          rollRad: newRollRad,
        });

        scheduleRender('VR', {
          type: 'chamberViewProps',
          chamberViewProps: {
            centers: chamberViewProps.centers!,
            viewNormal: res ? res.viewPlaneNormal : chamberViewProps.viewNormal,
            normal: res ? res.viewUp : chamberViewProps.normal,
          },
          newIndex: chamberViewProps.index,
          rotate: true,
        });
        // requestAnimationFrame(()=>{
        //   scheduleRender('Direction', {
        //     type: 'guideSliceMove',
        //     delta:0,
        //     viewportId: selectedPanelComponentKey!,
        //     isRef:false
        //   });
        // })
      }
      if (type === 'scoutDisplayRoll') {
        if (componentKey !== 'Direction') return;
        if (!refSVG.current) return;
        if (!selectedPanelComponentKey) return;
        const { movement, client } = args;
        const { chamberViewProps } = panelState;
        if (!chamberViewProps) return;
        showChamberRotationHint('scoutDisplayRoll');

        const distance = Math.hypot(movement[0], movement[1]);
        const g = refSVG.current?.querySelector('g');
        if (!g) return;
        const bbox = g.getBBox();
        const [cx, cy] = [bbox.x + bbox.width / 2, bbox.y + bbox.height / 2];
        const svgPoint = toSVGPoint({
          svg: refSVG.current,
          clientX: client[0],
          clientY: client[1],
        });
        const deg =
          (svgPoint.x - cx) * movement[1] - (svgPoint.y - cy) * movement[0] >=
          0
            ? -distance
            : distance;
        const rad = deg * (Math.PI / 180.0);
        const configKey = selectedPanelComponentKey as
          | 'FourChamber'
          | 'TwoChamber'
          | 'ShortAxis'
          | 'Axial';
        const newScoutDisplayRollRad =
          (chamberViewProps.scoutDisplayRollRad ?? 0) + rad;
        const nextChamberViewProps = {
          ...chamberViewProps,
          scoutDisplayRollRad: newScoutDisplayRollRad,
        };

        setData({
          chamberViewScoutDisplayRoll: {
            ...(config.chamberViewScoutDisplayRoll ?? {
              Axial: 0,
              FourChamber: 0,
              TwoChamber: 0,
              ShortAxis: 0,
            }),
            [configKey]: newScoutDisplayRollRad,
          },
        });
        updatePanelState(
          selectedPanelComponentKey,
          'chamberViewProps',
          nextChamberViewProps,
        );
        refreshDirectionGuideViewport(
          selectedPanelComponentKey,
          nextChamberViewProps,
        );
      }
      if (type === 'guideRoll') {
        // 여긴 반드시 가이드.

        if (componentKey !== 'Direction') return;
        if (!refSVG.current) throw '왜없';
        const { movement, client, isRef } = args;
        const { chamberViewProps } = panelState;
        if (!chamberViewProps) throw '왜없';

        const {
          normal: viewUp,
          viewNormal: viewPlaneNormal,
          flipVertical,
          flipHorizontal,
        } = chamberViewProps;
        if (!viewUp || !viewPlaneNormal) return;
        const distance = Math.hypot(movement[0], movement[1]);

        let rad = 0;
        const { rollRad, guideRollRad } = chamberViewProps;

        let deg: number = 0;

        const g = refSVG.current?.querySelector('g');
        if (!g) throw '왜없';
        const bbox = g?.getBBox()!;
        const [cx, cy] = [bbox.x + bbox.width / 2, bbox.y + bbox.height / 2];

        let newPlaneNormal = viewPlaneNormal;
        let newViewUp = viewUp;
        if (isRef) {
          showChamberRotationHint('guideRoll');
          const svgPoint = toSVGPoint({
            svg: refSVG.current!,
            clientX: client[0],
            clientY: client[1],
          });
          deg =
            (svgPoint.x - cx) * movement[1] - (svgPoint.y - cy) * movement[0] >=
            0
              ? -distance
              : distance;

          rad = deg * (Math.PI / 180.0);
          //누적
          const newGuideRollRad = guideRollRad + rad;
          setData({
            chamberCaptureGuideRollAngle: {
              ...config.chamberCaptureGuideRollAngle,
              [selectedPanelComponentKey! as
                | 'FourChamber'
                | 'TwoChamber'
                | 'ShortAxis'
                | 'Axial']: newGuideRollRad,
            },
          });

          newPlaneNormal = rotateAroundAxis(viewPlaneNormal, viewUp, rad);

          const { axis } = alignFunction(
            newPlaneNormal,
            newViewUp,
            chamberViewProps.alignAxis!,
          );

          const { index, centers } = getCenters({
            center: chamberViewProps.position!,
            spacing: chamberViewProps.spacing!,
            viewPlaneNormal: newPlaneNormal,
            pixelSpacing,
            imageSize: chamberViewProps.totalSpacing!,
            additionalSlice: toEffectiveAdditionalSlice(
              chamberViewProps.additionalSlice ?? 0,
            ),
            flip: config.chamberCaptureFlipDirection[
              selectedPanelComponentKey! as
                | 'FourChamber'
                | 'TwoChamber'
                | 'ShortAxis'
                | 'Axial'
            ],
          });

          const svg = getGuideLines({
            viewportId: selectedPanelComponentKey!,
            centers,
            axis: getOrthoNormal(newViewUp, newPlaneNormal),
            imageSize: chamberViewProps.imageSize!,
            pixelSpacing,
          });
          if(!svg) return;

          updateViewportCamera({
            viewportId: selectedPanelComponentKey!,
            focalPoint: centers[chamberViewProps.index],
            viewPlaneNormal: newPlaneNormal,
            // viewUp:newViewUp
            viewUp: axis,
            handleUpdatePanelState: (key, value) =>
              updateViewportPanelState(selectedPanelComponentKey!, key, value),
          });

          const res = lastChamberTransform(
            selectedPanelComponentKey!,
            rollRad,
            flipVertical,
            flipHorizontal,
            (key, value) =>
              updateViewportPanelState(selectedPanelComponentKey!, key, value),
          );

          const line = svg.querySelector('line#highlight')!;
          const refLine =
            svg.querySelector('g')!.children[chamberViewProps.index * 2]!;
          line.setAttribute('x1', refLine.getAttribute('x1')!);
          line.setAttribute('y1', refLine.getAttribute('y1')!);
          line.setAttribute('x2', refLine.getAttribute('x2')!);
          line.setAttribute('y2', refLine.getAttribute('y2')!);

          refSVG.current.innerHTML = '';
          refSVG.current.appendChild(svg);

          updatePanelState(selectedPanelComponentKey!, 'chamberViewProps', {
            ...chamberViewProps,
            guideRollRad: newGuideRollRad,
            centers,
            normal: newViewUp,
            viewNormal: newPlaneNormal,
          });

          scheduleRender('VR', {
            type: 'chamberViewProps',
            chamberViewProps: {
              centers,
              viewNormal: res ? res.viewPlaneNormal : newPlaneNormal,
              normal: res ? res.viewUp : axis,
            },
            newIndex: chamberViewProps.index,
            rotate: true,
          });
        } else {
          const vx = client[0] - cx;
          const vy = client[1] - cy;

          const px = vx - movement[0];
          const py = vy - movement[1];

          const cross = px * vy - py * vx;
          deg = cross >= 0 ? distance : -distance;

          const { flipVertical, flipHorizontal } = chamberViewProps;

          rad =
            (Boolean(Number(flipVertical) ^ Number(flipHorizontal))
              ? -deg
              : deg) *
            (Math.PI / 180.0);

          const newRollRad = rad + rollRad;

          const { axis } = alignFunction(
            chamberViewProps.viewNormal!,
            chamberViewProps.normal!,
            chamberViewProps.alignAxis!,
          );
          updateViewportCamera({
            viewportId: selectedPanelComponentKey!,
            focalPoint: chamberViewProps.centers![chamberViewProps.index!],
            viewPlaneNormal: chamberViewProps.viewNormal!,
            viewUp: axis,
            handleUpdatePanelState: (key, value) =>
              updateViewportPanelState(selectedPanelComponentKey!, key, value),
          });
          //델타만
          const res = lastChamberTransform(
            selectedPanelComponentKey!,
            newRollRad,
            flipVertical,
            flipHorizontal,
            (key, value) =>
              updateViewportPanelState(selectedPanelComponentKey!, key, value),
          );

          if (!res) return;
          if (!selectedPanelComponentKey) return;
          setData({
            chamberCaptureRollAngle: {
              ...config.chamberCaptureRollAngle,
              [selectedPanelComponentKey as
                | 'FourChamber'
                | 'TwoChamber'
                | 'ShortAxis'
                | 'Axial']: newRollRad,
            },
          });
          updatePanelState(selectedPanelComponentKey, 'chamberViewProps', {
            ...chamberViewProps,
            rollRad: newRollRad,
          });

          scheduleRender('VR', {
            type: 'chamberViewProps',
            chamberViewProps: {
              centers: chamberViewProps.centers!,
              viewNormal: res.viewPlaneNormal,
              normal: res.viewUp,
            },
            newIndex: chamberViewProps.index,
            rotate: true,
          });
        }

        // updateGuide(selectedPanelComponentKey!);
      }
      if (type === 'guidePan') {
        const { translation, isRef } = args;
        const { chamberViewProps } = panelState;
        if (!chamberViewProps) return;
        if (!refSVG.current) return;
        if (!selectedPanelComponentKey) return;
        const configKey = selectedPanelComponentKey as
          | 'FourChamber'
          | 'TwoChamber'
          | 'ShortAxis'
          | 'Axial';
        let movement = (
          isRef ? translation : translation.map((v: number) => -v)
        ) as [number, number, number];
        const nextPanState = resolveNextChamberGuidePanState({
          currentPosition: chamberViewProps.position!,
          currentGuideDelta: chamberViewProps.guideDelta,
          currentDirectionBackgroundFocalPoint:
            getChamberViewDirectionBackgroundFocalPoint(chamberViewProps),
          currentDirectionBackgroundMovement:
            config.chamberViewGuidePan[configKey].directionBackgroundMovement,
          movement,
          isRef,
        });
        if (isRef) {
          //제한
          if (nextPanState.guideDelta.some((v) => Math.abs(v) > 150)) {
            return;
          }
        }
        setData({
          chamberCaptureGuideMovement: {
            ...config.chamberCaptureGuideMovement,
            [configKey]: nextPanState.guideMovement,
          },
          chamberCaptureDirectionBackgroundMovement: {
            ...config.chamberCaptureDirectionBackgroundMovement,
            [configKey]: nextPanState.directionBackgroundMovement,
          },
          chamberViewGuidePan: {
            ...config.chamberViewGuidePan,
            [configKey]: {
              guideDelta: nextPanState.guideMovement,
              directionBackgroundMovement:
                nextPanState.directionBackgroundMovement,
            },
          },
        });
        const focalPoint = nextPanState.position;
        const directionBackgroundFocalPoint =
          nextPanState.directionBackgroundFocalPoint;
        const guideDelta = nextPanState.guideDelta;
        const { centers, index } = getCenters({
          center: focalPoint,
          spacing: chamberViewProps.spacing!,
          viewPlaneNormal: chamberViewProps.viewNormal!,
          pixelSpacing,
          imageSize: chamberViewProps.totalSpacing!,
          additionalSlice: toEffectiveAdditionalSlice(
            chamberViewProps.additionalSlice ?? 0,
          ),
          flip: config.chamberCaptureFlipDirection[
            selectedPanelComponentKey! as
              | 'FourChamber'
              | 'TwoChamber'
              | 'ShortAxis'
              | 'Axial'
          ],
        });

        if (!isRef) {
          updateViewportCameraDeltaMove({
            viewportId: 'Direction',
            delta: movement,
          });
        }
        const svg = getGuideLines({
          viewportId: selectedPanelComponentKey!,
          // baseIndex:index,
          centers: centers,
          axis: getOrthoNormal(
            chamberViewProps.normal!,
            chamberViewProps.viewNormal!,
          ),
          // viewPlaneNormal: chamberViewProps.viewNormal!,
          // spacing,
          imageSize: chamberViewProps.imageSize!,
          pixelSpacing,
        });
        if(!svg) return;

        refSVG.current.innerHTML = '';
        refSVG.current.appendChild(svg);
        const g = svg.querySelector('g');
        const line = svg.querySelector('line#highlight');
        if (!g || !line) return;
        // const bbox = g.getBBox()!
        // const [cx,cy] = [bbox.x + bbox.width / 2, bbox.y + bbox.height / 2];
        // const rollDeg = state.rollRad * (180 / Math.PI)

        const safeIndex = getSafeChamberIndex(
          chamberViewProps.index,
          centers.length,
        );
        if (safeIndex < 0) return;
        const refLine = g.children[safeIndex * 2];
        if (!refLine) return;

        line.setAttribute('x1', refLine.getAttribute('x1')!);
        line.setAttribute('y1', refLine.getAttribute('y1')!);
        line.setAttribute('x2', refLine.getAttribute('x2')!);
        line.setAttribute('y2', refLine.getAttribute('y2')!);

        const { axis } = alignFunction(
          chamberViewProps.viewNormal!,
          chamberViewProps.normal!,
          chamberViewProps.alignAxis!,
        );

        updateViewportCamera({
          viewportId: selectedPanelComponentKey!,
          focalPoint: centers[safeIndex],
          viewPlaneNormal: chamberViewProps.viewNormal!,
          // viewUp: chamberViewProps.normal!,
          viewUp: axis,
          handleUpdatePanelState: (key, value) =>
            updateViewportPanelState(selectedPanelComponentKey!, key, value),
        });
        const res = lastChamberTransform(
          selectedPanelComponentKey!,
          chamberViewProps.rollRad,
          chamberViewProps.flipVertical,
          chamberViewProps.flipHorizontal,
          (key, value) =>
            updateViewportPanelState(selectedPanelComponentKey!, key, value),
        );

        updatePanelState(selectedPanelComponentKey!, 'chamberViewProps', {
          ...withChamberViewDirectionBackgroundFocalPoint(
            {
              ...chamberViewProps,
              position: focalPoint,
              centers,
              guideDelta,
            },
            directionBackgroundFocalPoint,
            selectedSeriesId,
          ),
        });
        scheduleRender('VR', {
          type: 'chamberViewProps',
          chamberViewProps: {
            centers,
            viewNormal: res
              ? res.viewPlaneNormal
              : chamberViewProps.viewNormal!,
            normal: res ? res.viewUp : chamberViewProps.normal!,
          },
          newIndex: chamberViewProps.index,
          rotate: false,
          mode: type,
          moveAxes: isRef,
        });
      }
      if (type === 'guideSliceMove') {
        const { delta, nextIndex, viewportId, isRef } = args;
        if (!panelStates || !viewportId) return;
        const { chamberViewProps } =
          panelStates[viewportId as PanelComponentKeyType];
        if (!chamberViewProps) return;
        // console.log(delta);
        let newIndex = chamberViewProps.index!;

        // 요것은 일반적으로 guide에서 날뛰는 놈인겨.
        if (!isRef) {
          newIndex = delta ? newIndex - Math.sign(delta) : nextIndex;

          const g = refSVG.current?.querySelector('g');
          const line = refSVG.current?.querySelector('line#highlight');
          if (!g || !line) return;

          //빙글빙글
          const realNum = g.children.length / 2;
          if (newIndex >= realNum) newIndex = 0;
          if (newIndex < 0) newIndex = realNum - 1;
          const nextLine = g.children[newIndex * 2];
          if (nextLine === undefined) return;
          line.setAttribute('x1', nextLine.getAttribute('x1')!);
          line.setAttribute('y1', nextLine.getAttribute('y1')!);
          line.setAttribute('x2', nextLine.getAttribute('x2')!);
          line.setAttribute('y2', nextLine.getAttribute('y2')!);
        }
        const { axis } = alignFunction(
          chamberViewProps.viewNormal!,
          chamberViewProps.normal!,
          chamberViewProps.alignAxis!,
        );
        // const axis = chamberViewProps.normal!;

        let centers = chamberViewProps.centers;
        let position = chamberViewProps.position;
        // 여기는 centers를 딷딷딷 움직여줄것이여. 뭐로?
        if (isRef) {
          const movement = pixelSpacing.map(
            (ps, i) => ps * delta * axis[i],
          ) as typeof pixelSpacing;
          centers = centers!.map((center) => [
            center[0] + movement[0],
            center[1] + movement[1],
            center[2] + movement[2],
          ]);
          position = [
            position![0] + movement[0],
            position![1] + movement[1],
            position![2] + movement[2],
          ];

          updateViewportCameraDeltaMove({
            viewportId: 'Direction',
            delta: movement,
          });
        }

        const safeNewIndex = getSafeChamberIndex(newIndex, centers?.length ?? 0);
        if (safeNewIndex < 0) return;
        newIndex = safeNewIndex;

        updateViewportCamera({
          viewportId,
          focalPoint: centers![newIndex],
          viewPlaneNormal: chamberViewProps.viewNormal!,
          viewUp: axis,
          handleUpdatePanelState: (key, value) =>
            updateViewportPanelState(viewportId, key, value),
        });
        const res = lastChamberTransform(
          viewportId,
          chamberViewProps.rollRad,
          chamberViewProps.flipVertical,
          chamberViewProps.flipHorizontal,
          (key, value) => updateViewportPanelState(viewportId, key, value),
        );

        updatePanelState(viewportId, 'chamberViewProps', {
          ...chamberViewProps,
          centers,
          position,
          index: newIndex,
        });

        scheduleRender('VR', {
          type: 'chamberViewProps',
          chamberViewProps: {
            ...chamberViewProps,
            centers,
            position,
            viewNormal: res ? res.viewPlaneNormal : chamberViewProps.viewNormal,
            normal: res ? res.viewUp : chamberViewProps.normal,
          },
          newIndex,
          rotate: false,
          mode: type,
        });
      }

      if (type === 'guideUpdate' || type === 'guideZoom') {
        if (type === 'guideZoom') {
          args = { ...args, state: panelState.chamberViewProps };
        }
        console.log(panelStates.FourChamber.chamberViewProps)
        const { target: viewportId, state } = args;
        const guideTargetViewportId =
          (viewportId as PanelComponentKeyType | undefined) ??
          selectedPanelComponentKey;
        if (!refSVG.current) return;
        if (!state) return;
        if (!guideTargetViewportId) return;

        getInvisibleSection({
          viewportId: guideTargetViewportId,
          imageSize: state.imageSize!,
          pixelSpacing,
        });
        const { centers, index } = getCenters({
          center: state.position!,
          spacing: state.spacing!,
          viewPlaneNormal: state.viewNormal!,
          pixelSpacing,
          imageSize: state.totalSpacing!,
          additionalSlice: toEffectiveAdditionalSlice(state.additionalSlice ?? 0),
          flip: config.chamberCaptureFlipDirection[
            guideTargetViewportId as
              | 'FourChamber'
              | 'TwoChamber'
              | 'ShortAxis'
              | 'Axial'
          ],
        });
        const svg = getGuideLines({
          viewportId: guideTargetViewportId,
          // baseIndex:index,
          centers,
          axis: getOrthoNormal(state.normal, state.viewNormal),
          // viewUp:state.normal,
          // viewPlaneNormal: state.viewNormal,
          // spacing,
          imageSize: state.imageSize,
          pixelSpacing,
        });
        if(!svg) return;

        refSVG.current.innerHTML = '';
        refSVG.current.appendChild(svg);
        const g = svg.querySelector('g');
        const line = svg.querySelector('line#highlight');
        if (!g || !line) return;

        const isFlipDirection =
          panelState.chamberViewProps?.flipDirection !== state.flipDirection;

        const stateCentersLength = state.centers?.length ?? 0;
        const rawNowIndex =
          centers.length != stateCentersLength
            ? index
            : isFlipDirection
              ? stateCentersLength - panelState.chamberViewProps?.index! - 1 // 플립할땐 뒤집어
              : state.index;
        const nowIndex = getSafeChamberIndex(rawNowIndex, centers.length);
        if (nowIndex < 0) return;

        const refLine = g.children[nowIndex * 2];
        if (!refLine) return;
        line.setAttribute('x1', refLine.getAttribute('x1')!);
        line.setAttribute('y1', refLine.getAttribute('y1')!);
        line.setAttribute('x2', refLine.getAttribute('x2')!);
        line.setAttribute('y2', refLine.getAttribute('y2')!);

        // const transform =`rotate(${rollDeg}, ${cx}, ${cy})`;
        // g.setAttribute( 'transform', transform);
        // line.setAttribute('transform',transform);

        updatePanelState(
          guideTargetViewportId,
          'chamberViewProps',
          {
            ...state,
            centers,
            index: nowIndex,
            // index
          },
        );

        if (type === 'guideUpdate') {
          if (open['imageSize']) return;
          setData({
              chamberCaptureSliceHalfNumber: {
                ...config.chamberCaptureSliceHalfNumber,
                [guideTargetViewportId! as
                  | 'FourChamber'
                  | 'TwoChamber'
                  | 'ShortAxis'
                  | 'Axial']:clamp((centers.length - 1)/2,0,300)
              },
            })
          const { axis } = alignFunction(
            state.viewNormal!,
            state.normal!,
            state.alignAxis!,
          );
          updateViewportCamera({
            viewportId: guideTargetViewportId,
            focalPoint: centers![nowIndex],
            viewPlaneNormal: state.viewNormal!,
            viewUp: axis,
            handleUpdatePanelState: (key, value) =>
              updateViewportPanelState(guideTargetViewportId, key, value),
          });
          const res = lastChamberTransform(
            guideTargetViewportId,
            state.rollRad,
            state.flipVertical,
            state.flipHorizontal,
            (key, value) =>
              updateViewportPanelState(guideTargetViewportId, key, value),
          );

          scheduleRender('VR', {
            type: 'chamberViewProps',
            chamberViewProps: {
              ...state,
              centers,
              viewNormal: res ? res.viewPlaneNormal : state.viewNormal,
              normal: res ? res.viewUp : state.normal,
              // normal: args.target === 'Axial' ? state.normal : axis,
            },
            newIndex: nowIndex,
            rotate: true,
            mode: type,
            viewportId,
          });
        }
        // const {normal: viewUp,viewNormal: viewPlaneNormal, position: center,spacing,imageSize}=panelState.chamberViewProps
      }

      if (type === 'UpdateEF') {
        if (!ref.current) return;
        if (!pool || selectedStudySeries.length === 0) return;
        const { index } = args;
        currentEFSeriesIndex.current = index;
        const series = selectedStudySeries[index];
        addRequestPool({
          accessToken,
          series,
          volumeId: `PREVIEW_${series.id}`,
          filepath: `${series.id}_preview`,
          priority: 0,
          element: ref.current,
          viewportId: componentKey,
          slabThickness: panelState.thickness,
          thicknessMethod: panelState.thicknessMethod,
          postFunction() {
            updateEFViewport(index, handleUpdatePanelState);
          },
        });
      }

      if (type === 'uknowuknow') {
        if (!ref.current) return;
        updateModifierOverlay({ element: ref.current, ...args });
        // selectedSegKey.current = args.segKey;
      }
      if (type === 'reset') {
        if (analysisType === 'chamber') {
          if (!selectedPanelComponentKey) return;
          const state = panelStates[selectedPanelComponentKey].chamberViewProps;
          if (!state) return;

          const configKey = selectedPanelComponentKey as
            | 'FourChamber'
            | 'TwoChamber'
            | 'ShortAxis'
            | 'Axial';

          setData({
            chamberCaptureImageSize: {
              ...config.chamberCaptureImageSize,
              [configKey]: config.chamberCaptureDefaultImageSize,
            },
            chamberCaptureGuideRollAngle: {
              ...config.chamberCaptureGuideRollAngle,
              [configKey]: 0,
            },
            chamberCaptureRollAngle: {
              ...config.chamberCaptureRollAngle,
              [configKey]: 0,
            },
            chamberCaptureFlipVertical: {
              ...config.chamberCaptureFlipVertical,
              [configKey]: false,
            },
            chamberCaptureFlipHorizontal: {
              ...config.chamberCaptureFlipHorizontal,
              [configKey]: false,
            },
            chamberCaptureFlipDirection: {
              ...config.chamberCaptureFlipDirection,
              [configKey]: false,
            },
            chamberCaptureSpacing: {
              ...config.chamberCaptureSpacing,
              [configKey]: config.chamberCaptureDefaultSpacing,
            },
            chamberCaptureThickness: {
              ...config.chamberCaptureThickness,
              [configKey]: config.chamberCaptureDefaultThickness,
            },
            chamberCaptureThicknessMethod: {
              ...config.chamberCaptureThicknessMethod,
              [configKey]: config.chamberCaptureDefaultThicknessMethod,
            },
            chamberCaptureAdditionalSlice: {
              ...config.chamberCaptureAdditionalSlice,
              [configKey]: 0,
            },
            // chamberCaptureWindowLevel: config.chamberCaptureDefaultWindowLevel,
            // chamberCaptureWindowWidth: config.chamberCaptureDefaultWindowWidth,
            chamberCaptureGuideMovement: {
              ...config.chamberCaptureGuideMovement,
              [configKey]: [0, 0, 0] as [number, number, number],
            },
            chamberCaptureDirectionBackgroundMovement: {
              ...config.chamberCaptureDirectionBackgroundMovement,
              [configKey]: [0, 0, 0] as [number, number, number],
            },
            chamberViewGuidePan: {
              ...config.chamberViewGuidePan,
              [configKey]: {
                guideDelta: [0, 0, 0] as [number, number, number],
                directionBackgroundMovement: [0, 0, 0] as [
                  number,
                  number,
                  number,
                ],
              },
            },
            chamberViewScoutDisplayRoll:
              resetChamberScoutDisplayRollConfig(
                config.chamberViewScoutDisplayRoll,
                configKey,
              ),
          });

          if (
            !(pool?.parameter.data as CalciumAnalysisResult)?.SavedInfo?.Chamber
          ) {
            console.log('savedInfo.Chamber 없는데?');
            return;
          }

          const { center, viewPlaneNormal, viewUp } = parseChamberAxis({
            componentKey: selectedPanelComponentKey,
            savedInfo: (pool?.parameter.data as CalciumAnalysisResult)
              .SavedInfo,
          });

          const position = getWorldCoordinate(
            selectedPanelComponentKey,
            center,
            renderingQuality!,
          );
          const newState: ChamberViewProps =
            resetChamberScoutDisplayRollProps(
              withChamberViewDirectionBackgroundFocalPoint(
                {
                  ...state,
                  flipHorizontal: false,
                  flipVertical: false,
                  flipDirection: false,
                  additionalSlice: 0,
                  rollRad: 0,
                  guideRollRad: 0,
                  imageSize: 180,
                  totalSpacing: 180,
                  spacing: config.chamberCaptureDefaultSpacing,
                  position,
                  guideDelta: [0, 0, 0] as [number, number, number],
                },
                position,
                selectedSeriesId,
              ),
            );

          // const delta = [
          //   position[0] - state.position![0] - state.guideDelta![0],
          //   position[1] - state.position![1] - state.guideDelta![1],
          //   position[2] - state.position![2] - state.guideDelta![2],
          // ] as typeof position;

          // const halfWW = config.chamberCaptureDefaultWindowWidth * 0.5;
          // const lower = config.chamberCaptureDefaultWindowLevel - halfWW;
          // const upper = config.chamberCaptureDefaultWindowLevel + halfWW;
          // setVOIAll({lower,upper});

          //useTool의 ResetView에서 처리됨.
          // updateViewportsWindowLevelWidth(config.chamberCaptureDefaultWindowLevel,config.chamberCaptureDefaultWindowWidth)

          setupGuideViewport({
            viewportId: configKey,
            planeNormal: newState.guidePlaneNormal!,
            up: newState.guideUp!,
            focalPoint: position,
          });
          // updateViewportCameraDeltaMove({
          //   viewportId:'Direction',
          //   delta
          // });

          newState.normal = viewUp;
          newState.viewNormal = viewPlaneNormal;
          handleUpdatePanelState(
            'thickness',
            config.chamberCaptureDefaultThickness,
          );
          handleUpdatePanelState(
            'thicknessMethod',
            config.chamberCaptureDefaultThicknessMethod,
          );
          updatePanelState(
            selectedPanelComponentKey,
            'chamberViewProps',
            newState,
          );

          requestAnimationFrame(() => {
            scheduleRender('Direction', {
              type: 'guideUpdate',
              target: selectedPanelComponentKey,
              state: newState,
            });
            // guideUpdateCallback(state)
          });
        } else if (analysisType === 'ejectionFraction') {
          if (currentEFSeriesIndex.current === -1) return;
          updateEFViewport(
            currentEFSeriesIndex.current,
            handleUpdatePanelState,
          );
        }else {
        }
      }
      // if(type === 'visibility'){
      //   const {visible,name} = args;
      //   setVisibilityLabelSegmentation(componentKey,selectedSeriesId!,name,visible);
      // }
    },
  };

  useEffect(() => {
    if (switchConfigs.switchViewReport) return;
    // console.log(componentKey,analysisType,'리셋뷰포트?')
    if (isEF) return;
    // resetViewport('Axial',true);
  }, [switchConfigs.switchViewReport, analysisType]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (componentKey === 'Direction') return;

    const isPanPointerDown = (event: PointerEvent) => {
      if (event.ctrlKey || event.metaKey) return false;
      if (event.button === 2 || event.buttons === 2) return true;
      return event.button === 0 && panelToolState?.activeToolId === 'Pan';
    };

    const stopPanInteractionSoon = () => {
      window.setTimeout(() => {
        isDicomPanInteractionRef.current = false;
      }, 0);
    };

    const handlePointerDown = (event: PointerEvent) => {
      isDicomPanInteractionRef.current = isPanPointerDown(event);
    };
    const handlePointerUp = stopPanInteractionSoon;
    const handlePointerCancel = stopPanInteractionSoon;
    const handleWindowBlur = () => {
      isDicomPanInteractionRef.current = false;
    };

    element.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('pointercancel', handlePointerCancel, true);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      element.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('pointercancel', handlePointerCancel, true);
      window.removeEventListener('blur', handleWindowBlur);
      isDicomPanInteractionRef.current = false;
    };
  }, [componentKey, panelToolState?.activeToolId]);

  // 리슨
  useEffect(() => {
    if (!ref.current) return;
    // if (
    //   globalToolState?.toggles.ToggleSync &&
    //   syncPanelComponentKey != componentKey
    // )
    //   return;
    if (componentKey === 'Direction') return;
    // console.log(ref,refComponentKey,switchConfigs.switchSynchronizingPanel,syncPanelComponentKey)
    // 콜백님 바뀌셔야해요
    // 이친구, 대체할게 없다.
    const imageRendered = (evt: Event) =>
      onImageRendered(evt, refPanelState, handleUpdatePanelState);
    const cameraModified = (evt: Event) =>
      onCameraModified(evt, handleUpdatePanelState, {
        syncDicomZoom: isDicomZoomSyncEnabled,
        syncDicomPan:
          isDicomPanSyncEnabled && isDicomPanInteractionRef.current,
      });
    const handleMouseMove = (points?: [number, number, number, number]) => {
      if (points) {
        const scale = lowQuality ? 2 : 1;
        points[0] = Math.abs(Math.round(points[0] * scale)) + 1;
        points[1] = Math.abs(Math.round(points[1] * scale)) + 1;
        points[2] = Math.abs(Math.round(points[2] * scale)) + 1;
        setPointInfo(points);
      }
    };

    registerEvents({
      element: ref.current,
      imageRendered,
      handleMouseMove,
      cameraModified,
    });

    // handleVoiClampEvent({
    //   element: ref.current,
    //   selectedSeriesId,
    //   lastValidVoiRangeMap,
    // });
    // const remove = ()=>unregisterEvents(ref.current!,imageRendered,handleMouseMove,cameraModified)
    return () => {
      if (ref.current) {
        unregisterEvents({
          element: ref.current!,
          imageRendered,
          handleMouseMove,
          cameraModified,
        });
      }
    };
  }, [
    ref,
    refComponentKey,
    isDicomZoomSyncEnabled,
    isDicomPanSyncEnabled,
    // globalToolState?.toggles.ToggleSync,
    // syncPanelComponentKey,
  ]);

  // 픽섹, 브러시 훅
  useEffect(() => {
    if (!ref.current) return;
    updateModifierOverlay({ element: ref.current, pixelsPerMM });
    // if (isLock) {
    //   const allPanels: PanelComponentKeyType[] = [
    //     'Axial',
    //     'Coronal',
    //     'Sagittal',
    //   ];
    //   allPanels
    //     .filter((panel) => panel !== syncPanelComponentKey)
    //     .forEach((panel) => {
    //       scheduleRender(panel, { type: 'pixelChange' });
    //     });
    // }
  }, [pixelsPerMM]);

  //씽크모드
  //버그: refComponentKey가 Axial이 아닌경우에 의도치않은 동작
  useEffect(() => {
    registerSynchronizeViewport(componentKey);
    return () => {
      unregisterSynchronizeViewport();
    };
  }, []);

  // 뷰포트 셋업
  useEffect(() => {
    if (!ref.current) return;
    // 이미 있음. 각도만 초기값 알아서
    if (prevComponentKey === componentKey) return;

    if (!selectedSeriesId) return;
    // console.log(componentKey, prevComponentKey, '세덥');
    setPrevComponentKey(componentKey);
    setDICOMViewportVolume({
      viewportId: componentKey,
      element: ref.current,
      afterInitialize: () => {
        // 초기화 이후 행동인데, 뭔가 들어갈 수 있을듯
        // setIsEnabled(true);
      },
      seriesId: selectedSeriesId,
      slabThickness: panelState.thickness,
      thicknessMethod: panelState.thicknessMethod,
      lowQuality,
    });
    return () => {
      removeLabelmapViewport({ viewportId: componentKey });
    };
  }, [
    ref,
    prevComponentKey,
    // selectedSeriesId,analysisType
  ]);

  useEffect(() => {
    if (isEF) {
      return () => updateViewportOrientation({ viewportId: componentKey });
    }
  }, [analysisType]);

  // Reset displayed preview slice immediately on series change so the overlay
  // never shows the previous series index before the new viewport emits camera events.
  useEffect(() => {
    if (!selectedSeriesId) return;
    updatePanelState(componentKey, 'sliceIndex', 0);
  }, [selectedSeriesId, componentKey, updatePanelState]);

  // 시리즈 바뀌면 볼륨로드
  useEffect(() => {
    if (!ref.current) return;
    if (!selectedSeries) return;
    if (!selectedSeriesImageTag) return;

    const beLow = isEF || lowQuality;

    // config
    let volumeId = beLow
      ? `PREVIEW_${selectedSeriesId}`
      : `ORIGINAL_${selectedSeriesId}`;
    let filepath = beLow
      ? `${selectedSeriesId}_preview`
      : `${selectedSeriesId}_interpoled`;
    // handleLayoutStateLoading(true);
    addRequestPool({
      lowQuality: lowQuality,
      accessToken,
      series: selectedSeries,
      volumeId,
      filepath,
      priority: 1,
      postFunction: () => {
        // BrowserPageSelector에 ChamberView관련은 안해놔버리기.
        // 그래서 여기서 해버리기.

        if (analysisType === 'chamber') {
          if (componentKey !== 'Direction') {
            // 요것들은 서버에서 받을것, 다만 focalPoint는 scalar좌표에서 world로 바꿔야함
            // 나머지 축 뒤집기는 하부에서 처리

            const configKey = componentKey as
              | 'FourChamber'
              | 'TwoChamber'
              | 'ShortAxis'
              | 'Axial';
            const seriesScoutDisplayRollRad =
              config.chamberViewScoutDisplayRoll?.[configKey] ?? 0;

            let chamberViewProps: ChamberViewProps = {
              index: -1,
              flipVertical: false,
              flipHorizontal: false,
              flipDirection: false,
              additionalSlice: 0,
              rollRad: 0,
              guideRollRad: 0,
              scoutDisplayRollRad: seriesScoutDisplayRollRad,
              guideDelta: [0, 0, 0],
              imageSize: 180,
              totalSpacing: 180,
              spacing: 0,
              alignAxis: componentKey === 'Axial' ? [0, 1, 0] : [0, 0, -1],
            };

            if (!panelState.chamberViewProps) {
              if (
                !(pool?.parameter.data as CalciumAnalysisResult)?.SavedInfo
                  ?.Chamber
              ) {
                console.log('savedInfo.Chamber 없는데?');
                return;
              }
              let { center, viewPlaneNormal, viewUp } = parseChamberAxis({
                componentKey,
                savedInfo: (pool?.parameter.data as CalciumAnalysisResult)
                  .SavedInfo,
              });
              const focalPoint = getWorldCoordinate(
                componentKey,
                center,
                renderingQuality!,
              );
              const seriesGuidePan = resolveChamberGuidePanFromSeries({
                canonicalFocalPoint: focalPoint,
                guidePan: config.chamberViewGuidePan[configKey],
              });
              const directionBackgroundFocalPoint =
                seriesGuidePan.directionBackgroundFocalPoint;
              let res: ReturnType<typeof alignFunction> = {
                theta: 0,
                axis: viewPlaneNormal,
              };
              if (componentKey !== 'Axial') {
                res = alignFunction(
                  viewUp,
                  viewPlaneNormal,
                  chamberViewProps.alignAxis!,
                );
              }
              let spacing = config.chamberCaptureSpacing[configKey];
              if (spacing === 0) {
                spacing = config.chamberCaptureDefaultSpacing;
                setData({
                  chamberCaptureSpacing: {
                    Axial: config.chamberCaptureDefaultSpacing,
                    FourChamber: config.chamberCaptureDefaultSpacing,
                    ShortAxis: config.chamberCaptureDefaultSpacing,
                    TwoChamber: config.chamberCaptureDefaultSpacing,
                  },
                });
              }
              if (!config.chamberCaptureThickness[configKey]) {
                setData({
                  chamberCaptureThickness: {
                    Axial: config.chamberCaptureDefaultThickness,
                    FourChamber: config.chamberCaptureDefaultThickness,
                    ShortAxis: config.chamberCaptureDefaultThickness,
                    TwoChamber: config.chamberCaptureDefaultThickness,
                  },
                });
              }
              if (!config.chamberCaptureThicknessMethod[configKey]) {
                setData({
                  chamberCaptureThicknessMethod: {
                    Axial: config.chamberCaptureDefaultThicknessMethod,
                    FourChamber: config.chamberCaptureDefaultThicknessMethod,
                    ShortAxis: config.chamberCaptureDefaultThicknessMethod,
                    TwoChamber: config.chamberCaptureDefaultThicknessMethod,
                  },
                });
              }
              let imageSize = config.chamberCaptureImageSize[configKey];
              if (imageSize === 0) {
                imageSize = config.chamberCaptureDefaultImageSize;
                setData({
                  chamberCaptureImageSize: {
                    Axial: config.chamberCaptureDefaultImageSize,
                    FourChamber: config.chamberCaptureDefaultImageSize,
                    ShortAxis: config.chamberCaptureDefaultImageSize,
                    TwoChamber: config.chamberCaptureDefaultImageSize,
                  },
                });
              }
              const guideRollRad =
                config.chamberCaptureGuideRollAngle[configKey];
              if (!!guideRollRad) {
                viewPlaneNormal = rotateAroundAxis(
                  viewPlaneNormal,
                  viewUp,
                  guideRollRad,
                );
                chamberViewProps.guideRollRad = guideRollRad;
              }
              chamberViewProps.guideDelta = seriesGuidePan.guideDelta;

              (chamberViewProps.flipVertical =
                config.chamberCaptureFlipVertical[configKey]),
                (chamberViewProps.flipHorizontal =
                  config.chamberCaptureFlipHorizontal[configKey]),
                (chamberViewProps.flipDirection =
                  config.chamberCaptureFlipDirection[configKey]),
                (chamberViewProps.rollRad =
                  config.chamberCaptureRollAngle[configKey]),
                (chamberViewProps.additionalSlice =
                  config.chamberCaptureAdditionalSlice[configKey]),
                (chamberViewProps.spacing = spacing),
                (chamberViewProps.imageSize = imageSize);
              chamberViewProps.guideUp = res.axis;
              chamberViewProps.guidePlaneNormal = viewUp;
              chamberViewProps.position = seriesGuidePan.position;
              chamberViewProps.normal = viewUp;
              chamberViewProps.viewNormal = viewPlaneNormal;

              // 여기를 그냥 돌려줘서, centers 디팬던시 훅 작동시킴
              const { index, centers } = getCenters({
                center: chamberViewProps.position!,
                spacing: chamberViewProps.spacing!,
                viewPlaneNormal: chamberViewProps.viewNormal!,
                pixelSpacing,
                imageSize: chamberViewProps.totalSpacing!,
                additionalSlice: toEffectiveAdditionalSlice(
                  chamberViewProps.additionalSlice ?? 0,
                ),
                flip: config.chamberCaptureFlipDirection[configKey],
              });
              if (chamberViewProps.index === -1) chamberViewProps.index = index;
              chamberViewProps.centers = centers;
              chamberViewProps = withChamberViewDirectionBackgroundFocalPoint(
                chamberViewProps,
                directionBackgroundFocalPoint,
                selectedSeriesId,
              );
            } else {
              chamberViewProps = { ...panelState.chamberViewProps };

              let { center } = parseChamberAxis({
                componentKey,
                savedInfo: (pool?.parameter.data as CalciumAnalysisResult)
                  .SavedInfo,
              });

              const canonicalFocalPoint = getWorldCoordinate(
                componentKey,
                center,
                renderingQuality!,
              );
              const seriesGuidePan = resolveChamberGuidePanFromSeries({
                canonicalFocalPoint,
                guidePan: config.chamberViewGuidePan[configKey],
              });
              const existingDirectionBackgroundSeriesId =
                getChamberViewDirectionBackgroundSeriesId(
                  panelState.chamberViewProps,
                );
              const canReuseExistingGuidePan =
                !selectedSeriesId ||
                existingDirectionBackgroundSeriesId === selectedSeriesId;
              const reentryState = resolveChamberReentryState({
                existingPosition: panelState.chamberViewProps.position,
                existingGuideDelta: canReuseExistingGuidePan
                  ? panelState.chamberViewProps.guideDelta
                  : seriesGuidePan.guideDelta,
                existingDirectionBackgroundFocalPoint:
                  getChamberViewDirectionBackgroundFocalPoint(
                    panelState.chamberViewProps,
                  ),
                existingDirectionBackgroundSeriesId,
                currentSeriesId: selectedSeriesId,
                canonicalFocalPoint:
                  seriesGuidePan.directionBackgroundFocalPoint,
              });

              const { centers } = getCenters({
                center: reentryState.position,
                spacing: chamberViewProps.spacing!,
                viewPlaneNormal: chamberViewProps.viewNormal!,
                pixelSpacing,
                imageSize: chamberViewProps.totalSpacing!,
                additionalSlice: toEffectiveAdditionalSlice(
                  chamberViewProps.additionalSlice ?? 0,
                ),
                flip: config.chamberCaptureFlipDirection[configKey],
              });
              chamberViewProps = withChamberViewDirectionBackgroundFocalPoint(
                {
                  ...panelState.chamberViewProps,
                  position: reentryState.position,
                  guideDelta: reentryState.guideDelta,
                  scoutDisplayRollRad: seriesScoutDisplayRollRad,
                  centers,
                },
                reentryState.directionBackgroundFocalPoint,
                selectedSeriesId,
              );
            }
            //console.log(chamberViewProps.position)

            const { axis, theta } = alignFunction(
              chamberViewProps.viewNormal!,
              chamberViewProps.normal!,
              chamberViewProps.alignAxis!,
            );
            const safeIndex = getSafeChamberIndex(
              chamberViewProps.index,
              chamberViewProps.centers?.length ?? 0,
            );
            if (safeIndex < 0) return;
            chamberViewProps.index = safeIndex;
            const chamberElementRect = ref.current?.getBoundingClientRect();
            const chamberParallelScale = getChamberFitParallelScale({
              imageSize: chamberViewProps.imageSize,
              elementWidth: chamberElementRect?.width ?? 0,
              elementHeight: chamberElementRect?.height ?? 0,
              zoomScale,
            });

            updateViewportCamera({
              viewportId: componentKey,
              focalPoint: chamberViewProps.centers![safeIndex],
              viewPlaneNormal: chamberViewProps.viewNormal!,
              viewUp: axis,
              thickness: config.chamberCaptureThickness[configKey], //panelState.thickness,
              thicknessMethod: panelState.thicknessMethod,
              handleUpdatePanelState: (key, value) =>
                updateViewportPanelState(componentKey, key, value),
              parallelScale: chamberParallelScale,
            });

            lastChamberTransform(
              componentKey,
              chamberViewProps.rollRad,
              chamberViewProps.flipVertical,
              chamberViewProps.flipHorizontal,
              (key, value) =>
                updateViewportPanelState(componentKey, key, value),
            );

            updatePanelState(
              componentKey,
              'chamberViewProps',
              chamberViewProps,
            );
            registerToolGroupForChamber(
              componentKey,
              throttledUpdateWindowLevelWidth,
            );


            if (componentKey === 'Axial') {
              scheduleRender('VR', {
                type: 'chamberViewProps',
                chamberViewProps: {
                  centers: chamberViewProps.centers,
                  viewNormal: chamberViewProps.viewNormal,
                  normal: chamberViewProps.normal,
                },
                newIndex: chamberViewProps.index,
                rotate: true,
                run: true,
              });
            }
          }
        } else {
          registerToolGroupForViewport(
            componentKey,
            throttledUpdateWindowLevelWidth,
          );
          toggleCrosshairsTool(isAxisVisible ?? true);
        }
        console.log('굉장히 레지하죠?');
        if (isEF) {
          // updateEFViewPort(selectedStudySeries.findIndex(v=>v.id==seriesSelections[0].id));
        } else {
          // let windowLevel =
          // (panelState?.windowLevel ? panelState?.windowLevel
          //       : Number(selectedSeriesImageTag?.windowCenter ?? 600));
          // let windowWidth =
          // (panelState?.windowWidth ? panelState?.windowWidth
          //       : Number(selectedSeriesImageTag?.windowWidth ?? 800));

          let wl = windowLevel;
          let ww = windowWidth;
          // 무조껀 해버려
          if (analysisType === 'chamber') {
            requestAnimationFrame(() => {
              const configKey = componentKey as
                | 'FourChamber'
                | 'TwoChamber'
                | 'ShortAxis'
                | 'Axial';
              updateViewportThickness(
                componentKey,
                config.chamberCaptureThickness[configKey] * 0.5,
              );
              updateViewportThicknessMethod(
                componentKey,
                config.chamberCaptureThicknessMethod[
                  configKey
                ] as ThicknessMethodType,
              );
            });

            wl = config.chamberCaptureWindowLevel;
            ww = config.chamberCaptureWindowWidth;
            if (wl === 0 && ww === 0) {
              wl = windowLevel;
              ww = windowWidth;
              setData({
                chamberCaptureWindowLevel: wl,
                chamberCaptureWindowWidth: ww,
              });
            }
          }
          if (analysisType !== 'chamber') {
            fitDicomViewportsToScreenOnNextFrame();
          }
          // if(wl !== windowLevel || ww !== windowWidth){
          updateWindowLevelWidth([wl, ww]);
          updateVOIInvert(!!invert);
          // }
          // if(selectedAnalysisDefault){
          //   updateWindowLevelWidth([selectedAnalysisDefault.windowLevel,selectedAnalysisDefault.windowWidth]);
          // }else{
          //   updateWindowLevelWidth([wl,ww]);
          // }
          // console.log(ww,wl)
          // updateViewportsWindowLevelWidth(wl,ww);
          // updateViewportWindowLevelWidth(
          //   componentKey,
          //   windowLevel,
          //   windowWidth,
          // );
        }
        requestPixelsPerMM({
          viewportId: componentKey,
          handleUpdatePanelState,
        });
        requestAnimationFrame(() => {
          try {
            const viewport = getViewport(componentKey);
            const currentSliceIndex =
              viewport.getNumberOfSlices() - viewport.getSliceIndex() - 1;
            if (Number.isFinite(currentSliceIndex)) {
              handleUpdatePanelState('sliceIndex', currentSliceIndex);
            }
          } catch {
            // Keep the series-change reset value until Cornerstone emits camera events.
          }
        });
        if (analysisType === 'preview' || analysisType === 'MPR')
          handleLayoutStateLoading(false);
        // setIsEnabled(true);
        // scheduleRender('panel',{type:'checkDICOMPanelsEnabled'});
      }, //로드 이후에 행동,, 뭐 할게있나
      element: ref.current,
      viewportId: componentKey,
      slabThickness: panelState.thickness,
      thicknessMethod: panelState.thicknessMethod,
    });
    return () => {
      if (analysisType === 'chamber' && componentKey === 'Axial') {
        // coronal이나 sagittal꺼 있으면 가져오고 없으면 디폴트로 해
        // const wl = panelStates['Coronal'].windowLevel ??
        // panelStates['Sagittal'].windowLevel ??
        // Number(selectedSeriesImageTag?.windowCenter ?? 600);

        // const ww = panelStates['Coronal'].windowWidth ??
        // panelStates['Sagittal'].windowWidth ??
        // Number(selectedSeriesImageTag?.windowWidth ?? 800)

        // console.log('와따!',wl,ww)
        const thickness =
          panelStates['Coronal'].thickness ??
          panelStates['Sagittal'].thickness ??
          defaultPanelStates[componentKey]?.thickness;

        // 얘는 요렇게 안하면 조져지는 타이밍과 겹겹인듯
        requestAnimationFrame(() => {
          // updatePanelState('Axial', 'windowLevelWidth', {
          //   levelWidth: { label: '', windowLevel: wl, windowWidth: ww },
          // });
          handleUpdatePanelState('thickness', thickness);
        });
      }
      unregisterToolGroupForViewport(componentKey);
      detachVolume(componentKey);
    };
  }, [selectedSeriesId]);
  // useEffect(() => {
  //   if (switchConfigs.switchViewReport) return;
  //   //resetViewport('Axial');
  // }, [switchConfigs.switchViewReport]);

  useEffect(() => {
    // if (!(selectedTool?.id === 'resetView')){
    //   return;
    // }
    // if (!selectedPanelComponentKey) return;
    // const windowCenter = Number(selectedSeriesImageTag?.windowCenter);
    // const windowWidth = Number(selectedSeriesImageTag?.windowWidth);
    // if (
    //   !(
    //     [
    //       'Axial',
    //       'Coronal',
    //       'Sagittal',
    //       'Direction',
    //       'FourChamber',
    //       'TwoChamber',
    //       'ShortAxis',
    //     ] as PanelComponentKeyType[]
    //   ).includes(selectedPanelComponentKey)
    // )
    // updateViewportWindowLevelWidth(componentKey, windowCenter, windowWidth);
    // handleUpdatePanelState(
    //   'thickness',
    //   defaultPanelStates[componentKey]?.thickness ?? 0.5,
    // );
    // handleUpdatePanelState(
    //   'thicknessMethod',
    //   defaultPanelStates[componentKey]?.thicknessMethod ?? 'MIP',
    // );
    // handleUpdatePanelState('zoom', updateViewportZoom(componentKey, 0));
  }, [selectedTool]);

  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;
    setCanvasInfo({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    });
    // resizeViewport(componentKey)
    if (
      analysisType === 'chamber' &&
      isChamberDicomViewport(componentKey) &&
      panelState.chamberViewProps?.imageSize
    ) {
      fitChamberViewportToImageSize({
        viewportId: componentKey,
        imageSize: panelState.chamberViewProps.imageSize,
        zoomScale,
      });
      return;
    }
    fitDicomViewportsToScreenOnNextFrame();
  }, [
    analysisType,
    componentKey,
    dimensions,
    fitDicomViewportsToScreenOnNextFrame,
    panelState.chamberViewProps?.imageSize,
    zoomScale,
  ]);

  useEffect(() => {
    const wasEnabled = prevDicomZoomSyncEnabledRef.current;
    prevDicomZoomSyncEnabledRef.current = isDicomZoomSyncEnabled;
    if (!wasEnabled && isDicomZoomSyncEnabled) {
      fitDicomViewportsToScreenOnNextFrame();
    }
  }, [fitDicomViewportsToScreenOnNextFrame, isDicomZoomSyncEnabled]);

  // 라벨맵 갑니다잉.
  useEffect(() => {
    if (!selectedSeries) return;
    // console.log('엠?',prevComponentKey)
    if (prevComponentKey === 'Empty') return;
    if (
      analysisType === 'empty' ||
      analysisType === 'chamber' ||
      analysisType === 'preview' ||
      analysisType === 'MPR' ||
      isEF
    )
      return;
    const segmentation = panelStates?.Surface3D?.segmentation;
    const viewportSwitchPendingMap = config.viewportSwitchPendingMap;
    const viewportSwitchPending =
      viewportSwitchPendingMap[
        componentKey as keyof typeof viewportSwitchPendingMap
      ];
    if (!segmentation) return;
    let cleanupModifier: (() => void) | undefined;
    const cleanup = () => {
      removeLabelmapViewport({
        viewportId: componentKey,
        seriesId: selectedSeries.id,
      });
      if (isEF) {
        setPrevComponentKey('Empty');
      }
      cleanupModifier?.();
    };
    const updateManualViewport = () => {
      if (
        config.EditMode &&
        !viewportSwitchPending
      ) {
        requestAnimationFrame(() => {
          ensureManualSegmentationViewport(
            componentKey,
            selectedSeries.id,
            lowQuality,
          );
          setData((prev) => ({
            viewportSwitchPendingMap: {
              ...prev.viewportSwitchPendingMap,
              [componentKey]: true,
            },
          }));
        });
      }
    };

    scheduleRender('Surface3D', { type: 'dicomPanelReady' });
    console.log(
      '뷰포트가 있을진 모르겠는데 일단 받았어',
      componentKey,
      manualMode,
    );
    removeLabelmapViewport({
      viewportId: componentKey,
      seriesId: selectedSeries.id,
    });

    if (analysisType === 'FFR' && config.EditMode === EditNames.regulator) {
      updateRegulatorSegmentationViewport(componentKey, selectedSeries.id);
      updateManualViewport();
      return cleanup;
    }

    // s버튼 눌렀을때 값 세팅하는 코드

    if (viewportSwitchPending) {
      let clonedSegmentation = deepClone(segmentation) as Segmentation;
      const requestSegmentation = getRequestSegmentation(
        analysisType!,
        segmentation,
      );
      Object.keys(requestSegmentation).forEach((key) => {
        const segmentKey = key as keyof typeof requestSegmentation;
        clonedSegmentation = {
          ...clonedSegmentation,
          [segmentKey]: {
            ...clonedSegmentation[segmentKey],
            visibility:clonedSegmentation[segmentKey].visibility &&
                configStore.get().label.axial,
          },
        };
        updateLabelmapSegment({
          segmentTarget: segmentKey,
          segmentations: clonedSegmentation,
        });
      });

      updateLabelmapViewport(
        componentKey,
        selectedSeries.id,
        getRequestSegmentation(analysisType, clonedSegmentation),
        isEF || lowQuality,
      );
    }
    ////////////////////////////////////

    if (config.EditMode) {
      updateManualViewport();
    }
    return cleanup;
  }, [prevComponentKey, analysisType, selectedSeries, config.EditMode]);

  // 세그멘테이션 관련 - 끝
  // chamber view?

  const readyChamberViewGuide =
    !!selectedSeriesId &&
    !!selectedSeriesImageTag &&
    analysisType === 'chamber' &&
    componentKey === 'Direction';

  const updateGuide = useCallback(
    (targetViewportId: PanelComponentKeyType) => {
      // console.log('가이드업데이트',targetViewportId,panelStates[targetViewportId].chamberViewProps)
      const { chamberViewProps } = panelStatesRef.current[targetViewportId];
      if (!chamberViewProps) return;

      refreshDirectionGuideViewport(targetViewportId, chamberViewProps);
    },
    [refreshDirectionGuideViewport],
  );
  const selectedChamberViewProps = selectedPanelComponentKey
    ? panelStates[selectedPanelComponentKey]?.chamberViewProps
    : undefined;
  // 요기는 이벤트

  useEffect(() => {
    if (!readyChamberViewGuide) return;
    if (!selectedPanelComponentKey) return;
    // if (!isEnabled) return;
    // 그 챔버들만 눌릴것이요.
    const remover = setChamberViewEvents({
      targetViewportId: selectedPanelComponentKey,
      toolName: panelToolState?.activeToolId!,
    });
    updateGuide(selectedPanelComponentKey);
    const animationFrameId = requestAnimationFrame(() => {
      updateGuide(selectedPanelComponentKey);
    });
    return () => {
      cancelAnimationFrame(animationFrameId);
      remover();
    };
  }, [
    readyChamberViewGuide,
    selectedPanelComponentKey,
    panelToolState?.activeToolId,
    updateGuide,
  ]);

  useEffect(() => {
    if (!readyChamberViewGuide) return;
    if (!selectedPanelComponentKey) return;
    return () => {
      // 빨간테두리 없애야겠다.
      unsetInvisibleSection({ viewportId: selectedPanelComponentKey });
    };
  }, [readyChamberViewGuide, selectedPanelComponentKey]);

  // // 요기는 다시그리기
  // useEffect(()=>{
  //   // if(!refSVG.current)return;
  //   // if(!readyChamberViewGuide || !panelState.chamberViewProps) return;
  //   // if(!selectedPanelComponentKey || selectedPanelComponentKey === 'Direction' ||selectedPanelComponentKey==='Surface3D') return;
  //   // guideUpdateCallback(panelState.chamberViewProps)

  // },[refSVG,readyChamberViewGuide,selectedPanelComponentKey,
  //   // panelState.chamberViewProps,
  //   // panelState.chamberViewProps?.imageSize,
  //   // panelState.chamberViewProps?.additionalSliceCount,
  //   // panelState.chamberViewProps?.spacing
  // ])

  // 그리드가 없을때만 확인
  useEffect(() => {
    if (!readyChamberViewGuide) return;
    if (!selectedPanelComponentKey) return;
    if (!refSVG.current) return;
    if (!!refSVG.current.children.length) return;
    if (!selectedChamberViewProps) return;
    const animationFrameId = requestAnimationFrame(() => {
      updateGuide(selectedPanelComponentKey);
    });
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [
    readyChamberViewGuide,
    selectedPanelComponentKey,
    selectedChamberViewProps,
    updateGuide,
  ]);
  // console.log(componentKey,refSVG)
  //열/닫
  const handlePopupOpen = useCallback(
    (key: keyof DICOMPanelPopup) => {
      setOpen({ ...initialPopup, [key]: true });
    },
    [initialPopup],
  );
  const handlePopupClose = useCallback(
    (key: keyof DICOMPanelPopup) => {
      setOpen({ ...initialPopup, [key]: false });
    },
    [initialPopup],
  );

  const chamberAxisController = useMemo(
    () =>
      getChamberAxisControllerModel({
        analysisType,
        componentKey,
        state: chamberAxisState,
      }),
    [analysisType, componentKey, chamberAxisState],
  );

  const handleChamberAxisPickModeChange = useCallback(
    (pickMode: ChamberAxisPickMode) => {
      setChamberAxisPickMode(pickMode);
    },
    [],
  );

  const saveChamberAxisChamber4 = useCallback(
    (chamber4: number[]) => {
      if (!accessToken || !selectedSeriesId) {
        setChamberAxisError('save-failed');
        return;
      }

      void postSeriesParameterChamber4({
        accessToken,
        seriesId: selectedSeriesId,
        chamber4,
      }).catch((error) => {
        console.error('Failed to save chamber axis Chamber4', error);
        setChamberAxisError('save-failed');
      });
    },
    [accessToken, selectedSeriesId],
  );

  const applyChamberAxisSavedInfoToViewports = useCallback(
    (savedInfo: SavedInfo) => {
      let didApply = false;
      let selectedChamberProps: ChamberViewProps | undefined;
      const {
        guideViewportId: selectedChamberViewport,
        applyViewportId: selectedApplyViewport,
      } = resolveChamberAxisActiveViewport({ selectedPanelComponentKey });
      const missingViewport = CHAMBER_AXIS_APPLY_VIEWPORTS.find(
        (viewportId) => {
          const props = panelStates[viewportId].chamberViewProps;
          return (
            !props ||
            !props.alignAxis ||
            props.spacing === undefined ||
            props.totalSpacing === undefined
          );
        },
      );
      if (missingViewport) return false;

      CHAMBER_AXIS_APPLY_VIEWPORTS.forEach((viewportId) => {
        const currentProps = panelStates[viewportId].chamberViewProps!;
        const alignAxis = currentProps.alignAxis!;

        const { center, viewPlaneNormal, viewUp } = parseChamberAxis({
          componentKey: viewportId,
          savedInfo,
        });
        const position = getWorldCoordinate(
          viewportId,
          center,
          renderingQuality!,
        );
        const guideAxis = alignFunction(
          viewUp,
          viewPlaneNormal,
          alignAxis,
        ).axis;
        const seriesGuidePan = resolveChamberGuidePanFromSeries({
          canonicalFocalPoint: position,
          guidePan: config.chamberViewGuidePan[viewportId],
        });
        const directionBackgroundFocalPoint =
          seriesGuidePan.directionBackgroundFocalPoint;
        const guidePosition = seriesGuidePan.position;

        const nextProps: ChamberViewProps =
          withChamberViewDirectionBackgroundFocalPoint(
            {
              ...currentProps,
              position: guidePosition,
              guideDelta: seriesGuidePan.guideDelta,
              scoutDisplayRollRad:
                config.chamberViewScoutDisplayRoll?.[viewportId] ?? 0,
              normal: viewUp,
              viewNormal: viewPlaneNormal,
              guideUp: guideAxis,
              guidePlaneNormal: viewUp,
            },
            directionBackgroundFocalPoint,
            selectedSeriesId,
          );

        const { centers, index } = getCenters({
          center: nextProps.position!,
          spacing: nextProps.spacing!,
          viewPlaneNormal: nextProps.viewNormal!,
          pixelSpacing,
          imageSize: nextProps.totalSpacing!,
          additionalSlice: toEffectiveAdditionalSlice(
            nextProps.additionalSlice ?? 0,
          ),
          flip: config.chamberCaptureFlipDirection[viewportId],
        });
        const safeIndex = getSafeChamberIndex(
          typeof nextProps.index === 'number' ? nextProps.index : index,
          centers.length,
        );
        if (safeIndex < 0) return;

        nextProps.index = safeIndex;
        nextProps.centers = centers;

        const { axis } = alignFunction(
          nextProps.viewNormal!,
          nextProps.normal!,
          alignAxis,
        );
        updateViewportCamera({
          viewportId,
          focalPoint: centers[safeIndex],
          viewPlaneNormal: nextProps.viewNormal!,
          viewUp: axis,
          handleUpdatePanelState: (key, value) =>
            updateViewportPanelState(viewportId, key, value),
        });
        const res = lastChamberTransform(
          viewportId,
          nextProps.rollRad,
          nextProps.flipVertical,
          nextProps.flipHorizontal,
          (key, value) => updateViewportPanelState(viewportId, key, value),
        );

        updatePanelState(viewportId, 'chamberViewProps', nextProps);
        didApply = true;

        if (viewportId === selectedApplyViewport) {
          selectedChamberProps = nextProps;
          scheduleRender('VR', {
            type: 'chamberViewProps',
            chamberViewProps: {
              centers,
              viewNormal: res ? res.viewPlaneNormal : nextProps.viewNormal,
              normal: res ? res.viewUp : nextProps.normal,
            },
            newIndex: safeIndex,
            rotate: true,
          });
        }
      });

      if (selectedApplyViewport) {
        if (
          selectedChamberProps &&
          !refreshDirectionGuideViewport(
            selectedChamberViewport,
            selectedChamberProps,
          )
        ) {
          return false;
        }
      } else {
        const selectedGuideProps =
          panelStates[selectedChamberViewport].chamberViewProps;
        if (
          !selectedGuideProps ||
          !refreshDirectionGuideViewport(
            selectedChamberViewport,
            selectedGuideProps,
          )
        ) {
          return false;
        }
      }

      return didApply;
    },
    [
      config.chamberViewGuidePan,
      config.chamberViewScoutDisplayRoll,
      config.chamberCaptureFlipDirection,
      panelStates,
      pixelSpacing,
      refreshDirectionGuideViewport,
      renderingQuality,
      selectedPanelComponentKey,
      updatePanelState,
      updateViewportPanelState,
    ],
  );

  const handleChamberAxisReset = useCallback(() => {
    const savedInfo = (pool?.parameter.data as CalciumAnalysisResult | undefined)
      ?.SavedInfo;
    if (!savedInfo?.Chamber?.Chamber2 || !savedInfo.Chamber.Chamber4) {
      setChamberAxisError('missing-chamber-props');
      return;
    }

    const resetChamber4 = copyChamberAxisSavedInfoValue(
      savedInfo.Chamber.Chamber2,
    );
    if (!resetChamber4) {
      setChamberAxisError('missing-chamber-props');
      return;
    }

    savedInfo.Chamber.Chamber4 = resetChamber4;

    if (!applyChamberAxisSavedInfoToViewports(savedInfo)) {
      setChamberAxisError('missing-chamber-props');
      return;
    }

    resetChamberAxisState();
    saveChamberAxisChamber4(resetChamber4);
  }, [
    applyChamberAxisSavedInfoToViewports,
    pool?.parameter.data,
    saveChamberAxisChamber4,
  ]);

  const handleChamberAxisCalculate = useCallback(() => {
    const savedInfo = (pool?.parameter.data as CalciumAnalysisResult | undefined)
      ?.SavedInfo;
    if (!chamberAxisState.startWorld || !chamberAxisState.endWorld) {
      setChamberAxisError('missing-points');
      return;
    }
    if (!savedInfo?.Chamber?.Chamber2 || !savedInfo.Chamber.Chamber4) {
      setChamberAxisError('missing-chamber-props');
      return;
    }

    const result = calculateChamberAxisBasisRotation({
      startWorld: chamberAxisState.startWorld,
      endWorld: chamberAxisState.endWorld,
      basis: readChamberAxisBasis(savedInfo.Chamber.Chamber2),
    });

    if (result.status !== 'applied') {
      setChamberAxisError(result.reason);
      return;
    }

    if (!renderingQuality) {
      setChamberAxisError('missing-chamber-props');
      return;
    }

    const { guideViewportId: centerViewportId } =
      resolveChamberAxisActiveViewport({ selectedPanelComponentKey });
    const imageData = getViewport(centerViewportId)?.getImageData?.();
    const nextCenter = worldToChamberAxisCenterCoordinate({
      world: getChamberAxisMidpoint(
        chamberAxisState.startWorld,
        chamberAxisState.endWorld,
      ),
      origin: imageData?.origin as [number, number, number] | undefined,
      spacing: imageData?.spacing as [number, number, number] | undefined,
      scale: renderingQuality !== 'high' ? 0.5 : 1,
    });
    if (!nextCenter) {
      setChamberAxisError('missing-chamber-props');
      return;
    }

    const nextChamber4 = writeChamberAxisBasis(
      {
        ...result.basis,
        center: nextCenter,
      },
      savedInfo.Chamber.Chamber4,
    );
    savedInfo.Chamber.Chamber4 = nextChamber4;

    if (!applyChamberAxisSavedInfoToViewports(savedInfo)) {
      setChamberAxisError('missing-chamber-props');
      return;
    }

    markChamberAxisApplied(result.angleRad);
    saveChamberAxisChamber4(nextChamber4);
  }, [
    applyChamberAxisSavedInfoToViewports,
    chamberAxisState.endWorld,
    chamberAxisState.startWorld,
    pool?.parameter.data,
    renderingQuality,
    saveChamberAxisChamber4,
    selectedPanelComponentKey,
  ]);

  const handleChamberAxisViewportMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (analysisType !== 'chamber') return false;
      if (!isChamberAxisPickViewport(componentKey)) return false;
      if (chamberAxisState.pickMode === 'idle') return false;
      if (!ref.current) return false;

      const rect = ref.current.getBoundingClientRect();
      const world = getCanvasToWorld(componentKey, [
        event.clientX - rect.left,
        event.clientY - rect.top,
      ]);
      if (!world) return false;

      const worldPoint: [number, number, number] = [
        world[0],
        world[1],
        world[2],
      ];
      setChamberAxisWorldPoint(
        chamberAxisState.pickMode === 'pickingStart' ? 'start' : 'end',
        worldPoint,
      );
      event.preventDefault();
      event.stopPropagation();
      return true;
    },
    [analysisType, chamberAxisState.pickMode, componentKey],
  );

  const chamberAxisMarkers = useMemo(() => {
    if (analysisType !== 'chamber') return [];
    if (!isChamberAxisPickViewport(componentKey)) return [];

    return projectChamberAxisMarkers({
      state: chamberAxisState,
      worldToCanvas: (world) =>
        getCanvasCoordinatesFromWorld(componentKey, world),
    });
  }, [
    analysisType,
    chamberAxisState,
    componentKey,
    dimensions.height,
    dimensions.width,
    panelState.chamberViewProps?.centers,
    panelState.chamberViewProps?.index,
    panelState.chamberViewProps?.normal,
    panelState.chamberViewProps?.viewNormal,
    panelState.position,
    panelState.zoom,
  ]);

  // const projectionContextMenuItems: SimpleContextMenuItem[] = useMemo(
  //   () =>
  //     possibleProjection
  //       ?.filter((componentKey) => componentKey !== panelConfig.componentKey)
  //       ?.filter((componentKey) => (selectedLayoutConfig.panelConfig.some(panel => panel.componentKey === 'VR')))
  //       .map((componentKey) => ({
  //         label: componentKey,
  //         onClick: () => {
  //           swapLayoutPanelConfig(panelConfig.componentKey, componentKey);
  //           handlePopupClose('projection');
  //         },
  //         isChecked: panelConfig.componentKey === componentKey,
  //       })) ?? [],
  //   [open['projection']],
  // );

  const projectionContextMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const hasVR = selectedLayoutConfig.panelConfig.some(
      (panel) => panel.componentKey === 'VR',
    );
    const has3DSurface = selectedLayoutConfig.panelConfig.some(
      (panel) => panel.componentKey === 'Surface3D',
    );

    const isAvailableProjection = (componentKey: string) => {
      if (analysisType === 'segmentation') {
        if (hasVR && componentKey === 'Surface3D') return false;
        if (has3DSurface && componentKey === 'VR') return false;
      }
      if (analysisType === 'FFR') {
        if (!config.EditMode && componentKey === 'Surface3D') return false;
        if (config.EditMode) return false;
      }
      return true;
    };

    const filteredProjection = possibleProjection
      ?.filter((componentKey) => componentKey !== panelConfig.componentKey)
      ?.filter(isAvailableProjection);

    if(filteredProjection?.length === 0) {
      handlePopupClose('projection');
    }
    return (
      filteredProjection?.map((componentKey) => ({
        label: componentKey,
        onClick: () => {
          deactivateExclusiveTools({ viewportId: selectedPanelComponentKey });
           if (config.EditMode) {
            setData((prev) => ({
              viewportSwitchPendingMap: {
                ...prev.viewportSwitchPendingMap,
                [panelConfig.componentKey]: false,
                [componentKey]: false,
              },
            }));
          }
          swapLayoutPanelConfig(panelConfig.componentKey, componentKey);
          if(analysisType === 'segmentation')
            updateOtherViews([panelConfig.componentKey, componentKey]);

          handlePopupClose('projection');
        },
        isChecked: panelConfig.componentKey === componentKey,
      })) ?? []
    );
  }, [open['projection'], selectedLayoutConfig.panelConfig, analysisType]);

  //줌 아이템
  const zoomContextMenuItems: SimpleContextMenuItem[] = useMemo(
    () =>
      DICOMPanelZoomItems.map((item) => {
        return {
          label: item.name,
          onClick: () => {
            // setSelectedZoomToolId(item.id);
            handleToolActivate(item.id as ToolId);
            handlePopupClose('zoom');
          },
        };
      }),
    [open['zoom']],
  );

  const customPresetsObject = Object.fromEntries(
    (configStore.get().customWindowLevelPreset.presets ?? []).map((item) => [
      item.label,
      item,
    ]),
  );

  //프리셋 아이템
  const presetContextMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const presetMapper = (preset?: typeof defaultPreset) => {
      if (!preset) return [];
      return Object.entries(preset).map(([key, item]) => ({
        label: item.label,
        onClick: () => {
          if (!selectedSeriesId) return;
          let ww = item.windowWidth;
          let wl = item.windowLevel;
          let rawMin: number = 0;
          let rawMax: number = 0;
          if (key === 'default') {
            // if (analysisType === 'chamber') {
            //   ww = config.chamberCaptureDefaultWindowWidth;
            //   wl = config.chamberCaptureDefaultWindowLevel;
            // } else {
            ww = Number(selectedSeriesImageTag?.windowWidth);
            wl = Number(selectedSeriesImageTag?.windowCenter);
            // }
          } else if (key === 'fullDynamic') {
            [rawMin, rawMax] = getVolumeMinMax(
              selectedSeriesId,
              renderingQuality !== 'high',
            );
            ww = rawMax - rawMin;
            wl = (rawMax + rawMin) / 2;
          }
          const halfWW = ww * 0.5;
          rawMin = wl - halfWW;
          rawMax = wl + halfWW;

          let negative = invert;
          if (key !== 'negative') {
            updateWindowLevelWidth([wl, ww]);
            // updateViewportsWindowLevelWidth(wl,ww);
          } else {
            negative = !negative;
            updateVOIInvert(negative);
          }
          scheduleRender('VR', {
            type: 'updateWindowLevel',
            windowLevel: wl,
            windowWidth: ww,
            invert: negative,
          });

          // configStore.setData({
          //   selectedPreset: {
          //     label: item.label,
          //     windowWidth: ww,
          //     windowLevel: wl,
          //   },
          // });
          handlePopupClose('preset');
        },
        // 이거 삐져나오는데???
        isChecked: key === 'negative' && invert,
      }));
    };
    return [
      ...presetMapper(defaultPreset),
      ...presetMapper(customPresetsObject),
    ];
  }, [
    open['preset'],
    currentPreset,
    // panelState.windowLevel,panelState.windowWidth,
    windowLevel,
    windowWidth,
    invert,
    selectedSeriesId,
    selectedSeriesImageTag,
  ]);

  //프리셋 에디터
  const extraPresetContextMenuItem: SimpleContextMenuItem[] = useMemo(
    () => [
      {
        label: CT_PRESET_EDITOR_NAME,
        onClick: () => {
          handlePopupClose('preset');
          openCTPreset({
            componentKey: refComponentKey,
          });
        },
      },
    ],
    [
      open['preset'],
      currentPreset,
      windowLevel,
      windowWidth,
      handleUpdatePanelState,
    ],
  );
  //좌하단 내용물
  const bottomLeftContent = useMemo(
    () => (
      <>
        {/* MPR아니믄 Images 뿌려주기 */}
        {isPreview && panelState.sliceIndex !== undefined && (
          <div className="plain-text">{`Images: ${panelState.sliceIndex * (renderingQuality !== 'high' ? 2 : 1) + 1} / ${selectedSeriesImageTag?.depthInterpolated}`}</div>
        )}
        {
          <div
            className={
              isPreview
                ? `popup-text`
                : `projection-text ${componentKey === 'Direction' ? refComponentKey : componentKey}`
            }
            onClick={(e) => {
              handlePopupOpen('projection');
            }}
          >
            {componentKey === 'Direction' ? undefined : componentKey}
          </div>
        }
        {analysisType !== 'chamber' && (
          <div className={`popup-text`} onClick={() => handlePopupOpen('zoom')}>
            {panelState.zoom &&
              `${PANEL_ADJUSTMENT_NAMES.zoom}: ${(panelState.zoom * 100).toFixed(0)}%`}
          </div>
        )}

        {!config.EditMode && !manualMode && (
          <div
            className={`popup-text`}
            onClick={() => handlePopupOpen('thickness')}
            onWheel={(event) => {
              if (analysisType !== 'chamber') return;
              handleChamberOverlayWheel(
                event,
                panelState.thickness as number | undefined,
                CHAMBER_OVERLAY_WHEEL_RANGES.thickness,
                updateThicknessValue,
              );
            }}
          >
            {`${PANEL_ADJUSTMENT_NAMES.thickness} ${panelState.thicknessMethod}: ${!panelState.thickness || panelState.thickness < 0.5 ? 'MPR' : `${panelState.thickness.toFixed(1)}mm`}`}
          </div>
        )}
        {componentKey === 'Direction' && panelState.chamberViewProps && (
          <>
            <div
              className={`popup-text`}
              onClick={() => handlePopupOpen('spacing')}
              onWheel={(event) =>
                handleChamberOverlayWheel(
                  event,
                  panelState.chamberViewProps?.spacing,
                  CHAMBER_OVERLAY_WHEEL_RANGES.spacing,
                  updateChamberSpacingValue,
                )
              }
            >
              {panelState.chamberViewProps.spacing &&
                `${PANEL_ADJUSTMENT_NAMES.spacing}: ${panelState.chamberViewProps.spacing.toFixed(1)}mm`}
            </div>
            <div
              className={`popup-text`}
              onClick={() => handlePopupOpen('imageSize')}
              onWheel={(event) =>
                handleChamberOverlayWheel(
                  event,
                  panelState.chamberViewProps?.imageSize,
                  CHAMBER_OVERLAY_WHEEL_RANGES.imageSize,
                  updateChamberImageSizeValue,
                )
              }
            >
              {panelState.chamberViewProps.imageSize &&
                `${PANEL_ADJUSTMENT_NAMES.imageSize}: ${panelState.chamberViewProps.imageSize.toFixed(1)}mm`}
            </div>
          </>
        )}
      </>
    ),
    [
      isPreview,
      selectedPanelComponentKey,
      analysisType,
      componentKey,
      panelState.thicknessMethod,
      panelState.thickness,
      panelState.zoom,
      panelState.sliceIndex,
      panelState.chamberViewProps?.spacing,
      panelState.chamberViewProps?.imageSize,
      handleChamberOverlayWheel,
      updateChamberImageSizeValue,
      updateChamberSpacingValue,
      updateThicknessValue,
    ],
  );

  //우하단 내용물
  const bottomRightContent = useMemo(() => {
    let coordText: ReactElement | null = null;
    let sliceText: ReactElement | null = null;
    if (analysisType === 'chamber') {
      if (componentKey !== 'Direction') return;
      if (!panelState.chamberViewProps) return <></>;

      return (
        <>
          <div
            className="popup-text"
            onClick={() => {
              const state = panelState.chamberViewProps;
              if (!state) return;
              if (!selectedPanelComponentKey) return;

              setData({
                chamberCaptureFlipDirection: {
                  ...config.chamberCaptureFlipDirection,
                  [selectedPanelComponentKey! as
                    | 'FourChamber'
                    | 'TwoChamber'
                    | 'ShortAxis'
                    | 'Axial']: !state.flipDirection,
                },
              });
              guideUpdateCallback({
                ...panelState.chamberViewProps!,
                flipDirection: !state.flipDirection,
              });

              // guideUpdateCallback({
              //   ...panelState.chamberViewProps,
              //   additionalSlice,
              // });
            }}
          >
            Flip Direction
          </div>
          <div
            className="popup-text"
            onClick={() => handlePopupOpen('additionalSlice')}
            onWheel={(event) =>
              handleChamberOverlayWheel(
                event,
                panelState.chamberViewProps?.additionalSlice ?? 0,
                CHAMBER_OVERLAY_WHEEL_RANGES.additionalSlice,
                updateChamberAdditionalSliceValue,
              )
            }
          >
            {`Total Images: ${panelState.chamberViewProps.centers!.length}`}
          </div>
          <div className="popup-text" onClick={() => handlePopupOpen('preset')}>
            {`WL: ${formatWindowLevelOverlayValue(windowLevel)} WW: ${formatWindowLevelOverlayValue(windowWidth)}`}
          </div>
        </>
      );
    }

    if (panelState.sliceIndex !== undefined) {
      const displaySlice =
        panelState.sliceIndex * (renderingQuality !== 'high' ? 2 : 1) + 1;
      sliceText = (
        <div className="plain-text">{`Slice: ${Math.round(displaySlice)}`}</div>
      );
    }

    if (pointInfo) {
      const [x, y, z, v] = pointInfo;
      const [px, py] =
        componentKey === 'Axial'
          ? [x, y]
          : componentKey === 'Coronal'
            ? [x, z]
            : componentKey === 'Sagittal'
              ? [y, z]
              : [x, y];
      coordText = (
        <div className="plain-text">{`X: ${px} Y: ${py} HU: ${v}`}</div>
      );
    }
    return (
      <>
        {sliceText}
        {coordText}
        <div className="plain-text">{CTBottomRightSubInfo}</div>
        <div className="popup-text" onClick={() => handlePopupOpen('preset')}>
          {`WL: ${formatWindowLevelOverlayValue(windowLevel)} WW: ${formatWindowLevelOverlayValue(windowWidth)}`}
        </div>
      </>
    );
  }, [
    analysisType,
    windowLevel,
    windowWidth,
    // panelState.chamberViewProps?.centers?.length,
    panelState.chamberViewProps?.additionalSlice,
    panelState.chamberViewProps?.flipDirection,
    panelState.chamberViewProps?.flipHorizontal,
    panelState.chamberViewProps?.flipVertical,
    panelState.chamberViewProps?.rollRad,
    panelState.chamberViewProps?.guideRollRad,
    panelState.chamberViewProps?.scoutDisplayRollRad,
    panelState.chamberViewProps?.centers,
    panelState.sliceIndex,
    renderingQuality,
    pointInfo,
    handleChamberOverlayWheel,
    updateChamberAdditionalSliceValue,
  ]);

  // 기억해줘 스트링
  const memoizedClassName = useMemo(
    () =>
      `panel ${className}${fullscreenPanelComponentKey === componentKey ? ' fullscreen' : ''}`,
    [fullscreenPanelComponentKey, className],
  );

  // 더블 클릭시 전체 화면
  const hanldleFullScreenPanel = () => {
    if (isPreview) return;
    if (componentKey === fullscreenPanelComponentKey)
      updateFullscreenPanelComponentKey(undefined);
    else updateFullscreenPanelComponentKey(componentKey);
  };
  const isValidSvgText =
    fullscreenPanelComponentKey === 'Axial' ||
    fullscreenPanelComponentKey === 'Coronal' ||
    fullscreenPanelComponentKey === 'Sagittal' ||
    fullscreenPanelComponentKey === 'Empty' ||
    fullscreenPanelComponentKey === undefined;
  useEffect(() => {
    if (!worldInfo) return; // 이전에 계산된 canvas 좌표가 없으면 패스

    const canvasCoords = getCanvasCoordinatesFromWorld(componentKey, [
      worldInfo.x,
      worldInfo.y,
      worldInfo.z,
    ]);
    if (canvasCoords) {
      setCanvasInfo(canvasCoords);
    }
  }, [panelState.zoom, dimensions.width, panelState.position]);

  return (
    <div
      className={memoizedClassName}
      onMouseDown={(event) => {
        if (event.buttons===1&&handleChamberAxisViewportMouseDown(event)) return;
        if (componentKey === 'Direction') return;
        if (isToolInteracting()) return;
        if (
          selectedPanelComponentKey &&
          ['VR', 'Surface3D'].includes(selectedPanelComponentKey) &&
          panelToolState?.activeGroup === 'measurement'
        ) {
          // 일단은 기존에 사용한 tool에 측정도구일경우 다른 패널을 클릭하면진행중인 도구 취소
          deactivateExclusiveTools({ viewportId: componentKey });
          //handleToolActivate('Scroll');
        }

        updateSelectedPanelConfig({
          ...panelConfig,
          componentKey,
        });
      }}
      onDoubleClick={() => {
        if (Object.values(open).some(Boolean) || analysisType === 'chamber')
          return;
        hanldleFullScreenPanel();
      }}
      onWheel={(event)=>{
        if (event.deltaY===0)return;
        // if (handleChamberAxisViewportMouseDown(event)) return;
        if (componentKey === 'Direction') return;
        if (isToolInteracting()) return;
        if (selectedPanelComponentKey===componentKey)return;
        updateSelectedPanelConfig({
          ...panelConfig,
          componentKey,
        });
      }}
    >
      <div
        className={`renderer ${componentKey}`}
        ref={ref}
        // onMouseMove={handleMouseMove}
        // onMouseUp={handleMouseUp} onMouseDown={handleMouseDown}
        // style={{
        //   pointerEvents:'auto',
        // }}
      />
      <svg
        ref={refSVG}
        id="custom-overlay"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
          pointerEvents: 'none',
          opacity: globalToolState!.toggles.ToggleAnnotations ? 1 : 0,
        }}
      />
      {componentKey === 'Direction' && chamberRotationHint && panelToolState?.activeToolId === 'Rotation' && (
        <div className="chamber-rotation-hint">
          {getChamberRotationHintRows(chamberRotationHint).map((row) => (
            <div
              key={row.key}
              className={`chamber-rotation-hint__row${
                row.active ? ' is-active' : ''
              }`}
            >
              <span className="chamber-rotation-hint__command">
                {row.command}
              </span>
              <span className="chamber-rotation-hint__label">
                {row.label}
              </span>
            </div>
          ))}
        </div>
      )}
      {chamberAxisMarkers.length > 0 && (
        <div
          data-testid="chamber-axis-markers"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 3,
          }}
        >
          {chamberAxisMarkers.map((marker) => (
            <div
              key={marker.kind}
              title={`${marker.kind}: ${marker.world
                .map((value) => value.toFixed(1))
                .join(', ')}`}
              style={{
                position: 'absolute',
                left: marker.x,
                top: marker.y,
                width: 14,
                height: 14,
                marginLeft: -7,
                marginTop: -7,
                borderRadius: '50%',
                border: '2px solid #fff',
                background:
                  marker.kind === 'start'
                    ? 'rgba(64, 196, 255, 0.86)'
                    : 'rgba(255, 122, 89, 0.86)',
                color: '#111',
                fontSize: 9,
                fontWeight: 700,
                lineHeight: '10px',
                textAlign: 'center',
                boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.45)',
              }}
            >
              {marker.kind === 'start' ? 'S' : 'E'}
            </div>
          ))}
        </div>
      )}
      {globalToolState?.toggles.ToggleAnnotations && (
        <>
          <PanelOverlay
            config={{
              ...(analysisType !== 'chamber'
                ? {
                    'top-left': { content: topLeftContent },
                    'top-right': { content: topRightContent },
                    'bottom-right': { content: bottomRightContent },
                  }
                : { 'bottom-right': { content: bottomRightContent } }),
              'bottom-left': {
                content: (
                  <>
                    {midLeftContentRRPercent}
                    {bottomLeftContent}
                  </>
                ),
              },
            }}
            originComponentKey={componentKey}
            refComponentKey={refComponentKey}
          />
          {canvasInfo &&
            isAxisVisible &&
            singleCrosshairsAnalysisType(analysisType) &&
            isValidSvgText &&
            !getViewportIdsEnabled(['Axial','Coronal','Sagittal']) && (
              <Crosshair centerX={canvasInfo.x} centerY={canvasInfo.y} />
            )}
          {!isPreview && analysisType !== 'chamber' && (
            // forDistanceBar?.dimensions &&
            // forDistanceBar?.spacing?.[2] &&
            <DistanceScaleBarLayout
              // zoom={Number(panelState.zoom)}
              width={dimensions.width}
              height={dimensions.height}
              // dimensions={forDistanceBar.dimensions}
              // pixelSpacing={0.3}
              pixelsPerMM={pixelsPerMM}
              negativeColor={!!invert}
            />
          )}
          {open['projection'] && (
            <SimpleContextMenu
              items={projectionContextMenuItems}
              onClose={() => handlePopupClose('projection')}
            />
          )}
          {open['zoom'] && (
            <SimpleContextMenu
              items={zoomContextMenuItems}
              onClose={() => handlePopupClose('zoom')}
            />
          )}
          {open['thickness'] && (
            <ThicknessContextMenu
              value={panelState.thickness as number}
              method={
                panelState.thicknessMethod ??
                defaultPanelStates[componentKey]?.thicknessMethod ??
                'MIP'
              }
              minValue={0.2}
              onChangeValue={(value: number) => {
                updateThicknessValue(value);
              }}
              onChangeMethod={(method: ThicknessMethodType) => {
                if (analysisType === 'chamber') {
                  setData({
                    chamberCaptureThicknessMethod: {
                      ...config.chamberCaptureThicknessMethod,
                      [selectedPanelComponentKey! as
                        | 'FourChamber'
                        | 'TwoChamber'
                        | 'ShortAxis'
                        | 'Axial']: method,
                    },
                  });
                }
                handleUpdatePanelState('thicknessMethod', method);
              }}
              onReset={() => {
                if (analysisType === 'chamber') {
                  setData({
                    chamberCaptureThickness: {
                      ...config.chamberCaptureThickness,
                      [selectedPanelComponentKey! as
                        | 'FourChamber'
                        | 'TwoChamber'
                        | 'ShortAxis'
                        | 'Axial']: config.chamberCaptureDefaultThickness,
                    },
                  });
                  setData({
                    chamberCaptureThicknessMethod: {
                      ...config.chamberCaptureThicknessMethod,
                      [selectedPanelComponentKey! as
                        | 'FourChamber'
                        | 'TwoChamber'
                        | 'ShortAxis'
                        | 'Axial']: config.chamberCaptureDefaultThicknessMethod,
                    },
                  });
                  handleUpdatePanelState(
                    'thickness',
                    config.chamberCaptureDefaultThickness,
                  );
                  handleUpdatePanelState(
                    'thicknessMethod',
                    config.chamberCaptureDefaultThicknessMethod,
                  );
                } else {
                  handleUpdatePanelState(
                    'thickness',
                    defaultPanelStates[componentKey]?.thickness ?? 0.2,
                  );
                  handleUpdatePanelState(
                    'thicknessMethod',
                    defaultPanelStates[componentKey]?.thicknessMethod ?? 'MIP',
                  );
                }
              }}
              onClose={() => handlePopupClose('thickness')}
            />
          )}
          {open['preset'] && (
            <SimpleContextMenu
              items={presetContextMenuItems}
              ExtraActions={extraPresetContextMenuItem}
              onClose={() => handlePopupClose('preset')}
            />
          )}
          {/* Chamber  */}
          {/* <svg ref={refSVG}
            id='custom-overlay'
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: 0,
              top: 0,
              pointerEvents: 'none',
            }}
          /> */}
          {open['spacing'] && (
            <SpacingContextMenu
              value={panelState.chamberViewProps?.spacing as number}
              onChangeValue={(value: number) => {
                updateChamberSpacingValue(value);
              }}
              onReset={() => {
                setData({
                  chamberCaptureSpacing: {
                    ...config.chamberCaptureSpacing,
                    [selectedPanelComponentKey! as
                      | 'FourChamber'
                      | 'TwoChamber'
                      | 'ShortAxis'
                      | 'Axial']: config.chamberCaptureDefaultSpacing,
                  },
                });
                guideUpdateCallback({
                  ...panelState.chamberViewProps,
                  spacing: config.chamberCaptureDefaultSpacing,
                });
                // handleUpdatePanelState('spacing', spacing)
              }}
              onClose={() => handlePopupClose('spacing')}
            />
          )}
          {open['additionalSlice'] && (
            <AdditionalSliceContextMenu
              value={panelState.chamberViewProps?.additionalSlice as number}
              min={CHAMBER_ADDITIONAL_SLICE_MIN}
              max={CHAMBER_ADDITIONAL_SLICE_MAX}
              step={1}
              onChangeValue={(value: number) => {
                updateChamberAdditionalSliceValue(value);
              }}
              onReset={() => {
                const additionalSlice = clampAdditionalSlice(
                  defaultPanelStates[componentKey]?.chamberViewProps
                    ?.additionalSlice ?? 0.0,
                );
                if (
                  additionalSlice ==
                  panelState.chamberViewProps?.additionalSlice
                )
                  return;
                setData({
                  chamberCaptureAdditionalSlice: {
                    ...config.chamberCaptureAdditionalSlice,
                    [selectedPanelComponentKey! as
                      | 'FourChamber'
                      | 'TwoChamber'
                      | 'ShortAxis'
                      | 'Axial']: additionalSlice,
                  },
                });
                guideUpdateCallback({
                  ...panelState.chamberViewProps,
                  additionalSlice,
                });
                // handleUpdatePanelState('spacing', spacing)
              }}
              onClose={() => handlePopupClose('spacing')}
            />
          )}
          {open['imageSize'] && (
            <ImageSizeContextMenu
              value={panelState.chamberViewProps?.imageSize as number}
              onChangeValue={(value: number) => {
                updateChamberImageSizeValue(value);
              }}
              onReset={() => {
                // const imageSize = 180; // 이닛 위에있음
                setData({
                  chamberCaptureImageSize: {
                    ...config.chamberCaptureImageSize,
                    [selectedPanelComponentKey! as
                      | 'FourChamber'
                      | 'TwoChamber'
                      | 'ShortAxis'
                      | 'Axial']: config.chamberCaptureDefaultImageSize,
                  },
                });
                guideUpdateCallback({
                  ...panelState.chamberViewProps,
                  imageSize: config.chamberCaptureDefaultImageSize,
                });
                // handleUpdatePanelState('imageSize', imageSize);
              }}
              onClose={() => handlePopupClose('imageSize')}
            />
          )}
        </>
      )}
      <ChamberAxisController
        model={chamberAxisController}
        onPickModeChange={handleChamberAxisPickModeChange}
        onCalculate={handleChamberAxisCalculate}
        onReset={handleChamberAxisReset}
      />
    </div>
  );
};

export default DICOMPanel;
